import React from "react";
import {
  ActivityIndicator,
  Alert,
  Platform,
  Pressable,
  ScrollView,
  Text,
  View,
  useWindowDimensions,
} from "react-native";

import { SessionContext } from "../../../../contexts";
import { ADMIN_TOKENS } from "../../../../styles/adminTokens";
import { getPatients } from "../../../../services/api-clinic";
import { reprocessAudioAsDifferentNoteType } from "../../../../services/api-documentation";
import { mixpanel } from "../../../../utils/mixpanel";
import { Tab2, TitleText, ToastMessage } from "../../../common/native";
import { formatPhoneNumberDisplay } from "../../../../utils/phoneNumber";
import {
  assignCallToPatient,
  assignConversationToPatient,
  claimAvailablePhoneNumber,
  disableCommunicationsClinicUserAccess,
  enableCommunicationsClinicUserAccess,
  getCallNotes,
  getInboundVoiceRouting,
  getPhoneNumberPricing,
  getOrganizedClinicAudioForCalls,
  getOrganizedClinicNotesForCalls,
  getOutboundFromNumbers,
  getPatientAudioForCallDocs,
  getPatientNotesForCallDocs,
  hangupCall,
  listCommunicationsClinicUsersAccess,
  listAssignedPhoneNumbers,
  listAvailablePhoneNumbers,
  listCalls,
  setPrimaryPhoneNumber,
  TCommunicationsAssignedPhoneNumberItem,
  TCommunicationsCall,
  TCommunicationsCallId,
  TCommunicationsCallNote,
  TCommunicationsClinicUserAccess,
  TCommunicationsPhoneNumberInventoryItem,
  TCommunicationsPhoneNumberPricing,
  TCommunicationsRecording,
  TDocumentationAudioMetadata,
  TInboundVoiceRoutingConfiguration,
  unassignPhoneNumber,
  updateInboundVoiceRouting,
} from "../../../../services/api-communications";
import useCallSession from "./call-session/useCallSession";
import { styles } from "./HomeConversations.styles";
import { HOME_MOBILE_BREAKPOINT } from "../layout.constants";
import {
  AssignPatientModal,
  AssignPhoneOverwriteModal,
  CallDetailCard,
  CallRecordingsCard,
  CallsInboxCard,
  CommunicationsSetupCard,
  DialerCard,
  InboundCallRoutingCard,
  MessagesInboxCard,
  MessageThreadCard,
  PatientPhoneCallNotesCard,
  PhoneNumbersCard,
  UsageBillingCard,
} from "./components";
import type { TInboundRoutingMode } from "./components/InboundCallRoutingCard";
import {
  useCallDetail,
  useConversationMessages,
  useCommunicationsMutationRefresh,
  useDebouncedPatientSearch,
  useMessageComposer,
  useMessagingConversations,
  useUsageSummary,
} from "./hooks";
import {
  CALLS_PAGE_SIZE,
  COMMUNICATIONS_PATIENT_DISABLED_REASON,
  COMMUNICATIONS_SEAT_BILLING_COPY,
  DEFAULT_INCLUDED_MINUTES,
  DEFAULT_TO_COUNTRY_CODE,
  TMessageConversationListItem,
  TMessageSaveStatusFilter,
  TCommunicationsUiState,
  TPatientOption,
} from "./types";
import {
  formatCurrencyFromCents,
  formatDateTime,
  formatDuration,
  formatInteger,
  formatPhoneCapabilities,
  isCallActive,
  PENDING_HANGUP_STATUSES,
  toDateInputFromPeriodValue,
} from "./utils/formatters";
import {
  buildDialerE164Number,
  dialerE164LooksValid,
  findPatientByPhone,
  getPhoneDigits,
  normalizeDialerCountryCode,
  normalizeDialerLocalNumber,
  normalizePatientPhoneForCall,
  phonesLikelyMatch,
  splitDialerE164Number,
} from "./utils/phones";
import {
  mergePatientOptions,
  parseAssignedPhoneNumbersPayload,
  parseApiList,
  parseAvailablePhoneNumbersPayload,
  parsePricingPayload,
  recordingMatchesAudioSource,
  toMessageConversationListItems,
  toPatientOption,
} from "./utils/parsers";
import {
  getHttpErrorStatus,
  isAssignPhoneAlreadySetConflict,
  toUserFacingErrorMessage,
} from "./utils/errors";

const resolvePreferredFromE164 = (params: {
  current: string;
  fromNumbers: string[];
  defaultFromE164?: string | null;
}): string => {
  const { current, fromNumbers, defaultFromE164 } = params;
  const normalizedCurrent = (current || "").trim();
  if (normalizedCurrent && fromNumbers.includes(normalizedCurrent)) {
    return normalizedCurrent;
  }

  return (
    (defaultFromE164 || "").trim() ||
    (fromNumbers[0] || "").trim() ||
    normalizedCurrent
  );
};

const FEEDBACK_TOAST_DURATION_MS = 2500;
const DEFAULT_INBOUND_ROUTING_TIMEOUT_SECONDS = 30;

type TInboundRoutingFieldErrors = {
  phoneTarget?: string;
  clinicUserUuid?: string;
  timeoutSeconds?: string;
  save?: string;
};

const toInboundRoutingTimeoutInput = (value: number): string => {
  if (!Number.isFinite(value)) {
    return String(DEFAULT_INBOUND_ROUTING_TIMEOUT_SECONDS);
  }
  return String(Math.max(1, Math.min(120, Math.trunc(value))));
};

const normalizeTimeoutSecondsInput = (value: string): string =>
  (value || "").replace(/\D/g, "").slice(0, 3);

const resolveInboundRoutingModeFromConfig = (
  config: TInboundVoiceRoutingConfiguration,
): TInboundRoutingMode => {
  const activeMembers = (config.members || []).filter(
    (member) => member.active !== false,
  );
  const phoneMember = activeMembers.find(
    (member) => member.target_type === "phone_e164",
  );
  const clinicUserMember = activeMembers.find(
    (member) => member.target_type === "clinic_user",
  );

  if (phoneMember && clinicUserMember) {
    return "simultaneous";
  }
  if (clinicUserMember) {
    return "webapp_only";
  }
  return "phone_only";
};

const isConversationUnread = (conversation: {
  unread_count?: number | null;
  is_read?: boolean | null;
}): boolean => {
  const unreadCount = Math.max(conversation.unread_count || 0, 0);
  if (typeof conversation.is_read === "boolean") {
    return !conversation.is_read || unreadCount > 0;
  }
  return unreadCount > 0;
};

const resolveCallClinicOwnedE164 = (
  call: TCommunicationsCall,
  clinicOwnedNumbers: Set<string>,
): string => {
  const fromE164 = (call.from_e164 || "").trim();
  const toE164 = (call.to_e164 || "").trim();
  const candidateNumbers = [fromE164, toE164].filter(Boolean);

  if (!clinicOwnedNumbers.size) {
    return fromE164 || toE164;
  }

  return candidateNumbers.find((value) => clinicOwnedNumbers.has(value)) || "";
};

const HomeConversations: React.FC = () => {
  const session = React.useContext(SessionContext)?.session;
  const callSession = useCallSession();
  const { width } = useWindowDimensions();

  const isMobile = width < HOME_MOBILE_BREAKPOINT;
  const isClinicAdmin = session?.role === "CLINIC_ADMIN";

  const [toCountryCode, setToCountryCode] = React.useState<string>(
    DEFAULT_TO_COUNTRY_CODE,
  );
  const [toLocalNumber, setToLocalNumber] = React.useState<string>("");
  const [fromE164, setFromE164] = React.useState("");
  const [messageFromE164, setMessageFromE164] = React.useState("");
  const [patientUuid, setPatientUuid] = React.useState("");
  const [patientSearchTerm, setPatientSearchTerm] = React.useState("");
  const [showUnreadMessagesOnly, setShowUnreadMessagesOnly] =
    React.useState<boolean>(false);
  const [selectedMessageSaveStatuses, setSelectedMessageSaveStatuses] =
    React.useState<TMessageSaveStatusFilter[]>(["saved", "unsaved"]);
  const [
    selectedMessageAssignedNumberE164,
    setSelectedMessageAssignedNumberE164,
  ] = React.useState<string | null>(null);
  const [selectedCallAssignedNumberE164, setSelectedCallAssignedNumberE164] =
    React.useState<string | null>(null);
  const [isComposingNewMessage, setIsComposingNewMessage] =
    React.useState<boolean>(false);
  const [messageToCountryCode, setMessageToCountryCode] =
    React.useState<string>(DEFAULT_TO_COUNTRY_CODE);
  const [messageToLocalNumber, setMessageToLocalNumber] =
    React.useState<string>("");
  const [messageComposerDraft, setMessageComposerDraft] =
    React.useState<string>("");
  const [hangupSubmitting, setHangupSubmitting] =
    React.useState<boolean>(false);

  const [fromNumbers, setFromNumbers] = React.useState<string[]>([]);
  const [patients, setPatients] = React.useState<TPatientOption[]>([]);
  const [calls, setCalls] = React.useState<TCommunicationsCall[]>([]);
  const [callsTotal, setCallsTotal] = React.useState<number>(0);
  const [loadingMoreCalls, setLoadingMoreCalls] =
    React.useState<boolean>(false);
  const [callsPaginationError, setCallsPaginationError] = React.useState<
    string | null
  >(null);
  const [selectedCallId, setSelectedCallId] =
    React.useState<TCommunicationsCallId | null>(null);
  const [reprocessingRecordingId, setReprocessingRecordingId] = React.useState<
    number | null
  >(null);
  const [expandedCallNoteSummaries, setExpandedCallNoteSummaries] =
    React.useState<Record<string, boolean>>({});

  const [organizedAudioSummary, setOrganizedAudioSummary] = React.useState<{
    totalPatients: number;
    totalAudioFiles: number;
    uncategorizedCount: number;
  } | null>(null);
  const [organizedNotesSummary, setOrganizedNotesSummary] = React.useState<{
    totalPatients: number;
    totalNotes: number;
    uncategorizedCount: number;
  } | null>(null);

  const [initialLoading, setInitialLoading] = React.useState<boolean>(true);
  const [refreshingCalls, setRefreshingCalls] = React.useState<boolean>(false);

  const [pageError, setPageError] = React.useState<string | null>(null);
  const [assignModalVisible, setAssignModalVisible] =
    React.useState<boolean>(false);
  const [assignTargetCallId, setAssignTargetCallId] =
    React.useState<TCommunicationsCallId | null>(null);
  const [assignTargetConversationUuid, setAssignTargetConversationUuid] =
    React.useState<string | null>(null);
  const [assignPatientSearchTerm, setAssignPatientSearchTerm] =
    React.useState<string>("");
  const [assignSelectedPatientUuid, setAssignSelectedPatientUuid] =
    React.useState<string>("");
  const [assignSubmitting, setAssignSubmitting] =
    React.useState<boolean>(false);
  const [assignFeedback, setAssignFeedback] = React.useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);
  const [assignPhoneOverwritePrompt, setAssignPhoneOverwritePrompt] =
    React.useState<{
      target:
        | { type: "call"; callId: TCommunicationsCallId }
        | { type: "conversation"; conversationUuid: string };
      patient: TPatientOption;
    } | null>(null);

  const [communicationsPricing, setCommunicationsPricing] =
    React.useState<TCommunicationsPhoneNumberPricing | null>(null);
  const [communicationsPricingLoading, setCommunicationsPricingLoading] =
    React.useState<boolean>(true);
  const [communicationsPricingError, setCommunicationsPricingError] =
    React.useState<string | null>(null);
  const [assignedPhoneNumbers, setAssignedPhoneNumbers] = React.useState<
    TCommunicationsAssignedPhoneNumberItem[]
  >([]);
  const [assignedNumbersLoading, setAssignedNumbersLoading] =
    React.useState<boolean>(true);
  const [assignedNumbersError, setAssignedNumbersError] = React.useState<
    string | null
  >(null);
  const [availablePhoneNumbers, setAvailablePhoneNumbers] = React.useState<
    TCommunicationsPhoneNumberInventoryItem[]
  >([]);
  const [availableNumbersLoading, setAvailableNumbersLoading] =
    React.useState<boolean>(true);
  const [availableNumbersError, setAvailableNumbersError] = React.useState<
    string | null
  >(null);
  const [activationSubmitting, setActivationSubmitting] =
    React.useState<boolean>(false);
  const [claimingPhoneNumberId, setClaimingPhoneNumberId] = React.useState<
    number | null
  >(null);
  const [updatingPrimaryNumberId, setUpdatingPrimaryNumberId] = React.useState<
    number | null
  >(null);
  const [unassigningPhoneNumberId, setUnassigningPhoneNumberId] =
    React.useState<number | null>(null);
  const [communicationsActionFeedback, setCommunicationsActionFeedback] =
    React.useState<{
      type: "success" | "error";
      message: string;
    } | null>(null);
  const [
    communicationsAccessActionFeedback,
    setCommunicationsAccessActionFeedback,
  ] = React.useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);
  const [availableNumberSearchTerm, setAvailableNumberSearchTerm] =
    React.useState<string>("");
  const [communicationsAccessUsers, setCommunicationsAccessUsers] =
    React.useState<TCommunicationsClinicUserAccess[]>([]);
  const [communicationsAccessTotal, setCommunicationsAccessTotal] =
    React.useState<number>(0);
  const [
    communicationsAccessBillableSeatCount,
    setCommunicationsAccessBillableSeatCount,
  ] = React.useState<number>(0);
  const [communicationsAccessLoading, setCommunicationsAccessLoading] =
    React.useState<boolean>(false);
  const [communicationsAccessError, setCommunicationsAccessError] =
    React.useState<string | null>(null);
  const [
    mutatingCommunicationsAccessUserUuid,
    setMutatingCommunicationsAccessUserUuid,
  ] = React.useState<string | null>(null);
  const [selectedRoutingPhoneNumberId, setSelectedRoutingPhoneNumberId] =
    React.useState<number | null>(null);
  const [inboundRoutingLoading, setInboundRoutingLoading] =
    React.useState<boolean>(false);
  const [inboundRoutingSaving, setInboundRoutingSaving] =
    React.useState<boolean>(false);
  const [inboundRoutingError, setInboundRoutingError] = React.useState<
    string | null
  >(null);
  const [inboundRoutingMode, setInboundRoutingMode] =
    React.useState<TInboundRoutingMode>("phone_only");
  const [inboundRoutingEnabled, setInboundRoutingEnabled] =
    React.useState<boolean>(false);
  const [
    inboundRoutingPhoneTargetCountryCode,
    setInboundRoutingPhoneTargetCountryCode,
  ] = React.useState<string>(DEFAULT_TO_COUNTRY_CODE);
  const [
    inboundRoutingPhoneTargetLocalNumber,
    setInboundRoutingPhoneTargetLocalNumber,
  ] = React.useState<string>("");
  const [inboundRoutingClinicUserUuid, setInboundRoutingClinicUserUuid] =
    React.useState<string>("");
  const [inboundRoutingTimeoutSeconds, setInboundRoutingTimeoutSeconds] =
    React.useState<string>(String(DEFAULT_INBOUND_ROUTING_TIMEOUT_SECONDS));
  const [inboundRoutingFieldErrors, setInboundRoutingFieldErrors] =
    React.useState<TInboundRoutingFieldErrors>({});
  const [selectedCallsTab, setSelectedCallsTab] = React.useState<
    "Calls" | "Config" | "Usage" | "Messages"
  >("Calls");
  const assignFeedbackTimerRef = React.useRef<ReturnType<
    typeof setTimeout
  > | null>(null);
  const communicationsFeedbackTimerRef = React.useRef<ReturnType<
    typeof setTimeout
  > | null>(null);
  const communicationsAccessFeedbackTimerRef = React.useRef<ReturnType<
    typeof setTimeout
  > | null>(null);
  const markingReadConversationUuidsRef = React.useRef<Set<string>>(new Set());
  const isCallsTab = selectedCallsTab === "Calls";
  const isConfigTab = selectedCallsTab === "Config";
  const isUsageTab = selectedCallsTab === "Usage";
  const isMessagesTab = selectedCallsTab === "Messages";
  const ftuiViewedTrackedRef = React.useRef<boolean>(false);
  const overageTrackedStateRef = React.useRef<boolean | null>(null);

  React.useEffect(() => {
    if (!assignFeedback) {
      return;
    }
    if (assignFeedbackTimerRef.current) {
      clearTimeout(assignFeedbackTimerRef.current);
    }
    assignFeedbackTimerRef.current = setTimeout(() => {
      setAssignFeedback(null);
    }, FEEDBACK_TOAST_DURATION_MS);

    return () => {
      if (assignFeedbackTimerRef.current) {
        clearTimeout(assignFeedbackTimerRef.current);
      }
    };
  }, [assignFeedback]);

  React.useEffect(() => {
    if (!communicationsActionFeedback) {
      return;
    }
    if (communicationsFeedbackTimerRef.current) {
      clearTimeout(communicationsFeedbackTimerRef.current);
    }
    communicationsFeedbackTimerRef.current = setTimeout(() => {
      setCommunicationsActionFeedback(null);
    }, FEEDBACK_TOAST_DURATION_MS);

    return () => {
      if (communicationsFeedbackTimerRef.current) {
        clearTimeout(communicationsFeedbackTimerRef.current);
      }
    };
  }, [communicationsActionFeedback]);

  React.useEffect(() => {
    if (!communicationsAccessActionFeedback) {
      return;
    }
    if (communicationsAccessFeedbackTimerRef.current) {
      clearTimeout(communicationsAccessFeedbackTimerRef.current);
    }
    communicationsAccessFeedbackTimerRef.current = setTimeout(() => {
      setCommunicationsAccessActionFeedback(null);
    }, FEEDBACK_TOAST_DURATION_MS);

    return () => {
      if (communicationsAccessFeedbackTimerRef.current) {
        clearTimeout(communicationsAccessFeedbackTimerRef.current);
      }
    };
  }, [communicationsAccessActionFeedback]);

  const {
    usageStartDate,
    usageEndDate,
    usageSummary,
    usageLoading,
    usageError,
    setUsageStartDate,
    setUsageEndDate,
    loadUsageSummary,
    loadUsageSummaryForBillingPeriod,
  } = useUsageSummary({ session });
  const {
    selectedCall,
    selectedRecordings,
    selectedCallNotes,
    patientAudio,
    patientNotes,
    callDetailLoading,
    callDetailError,
    setSelectedCall,
    setSelectedCallNotes,
    setPatientAudio,
    setCallDetailError,
    loadCallDetail,
  } = useCallDetail({
    session,
    selectedCallId,
    activeCallId: callSession.activeCallContext?.callId || null,
    activeCallClientRequestId:
      callSession.activeCallContext?.clientRequestId || null,
    setCalls,
  });

  const selectedPatient = React.useMemo(
    () => patients.find((patient) => patient.uuid === patientUuid) || null,
    [patientUuid, patients],
  );
  const handleAssignPatientSearchResults = React.useCallback(
    (nextPatientOptions: TPatientOption[]) => {
      setPatients((currentPatients) =>
        mergePatientOptions(currentPatients, nextPatientOptions),
      );
    },
    [],
  );
  const patientSearch = useDebouncedPatientSearch({
    session,
    searchTerm: patientSearchTerm,
  });
  const assignPatientSearch = useDebouncedPatientSearch({
    session,
    searchTerm: assignPatientSearchTerm,
    enabled: assignModalVisible,
    onResults: handleAssignPatientSearchResults,
  });
  const patientNameByUuid = React.useMemo(() => {
    const nextMap = new Map<string, string>();

    patients.forEach((patient) => {
      const normalizedUuid = (patient.uuid || "").trim();
      if (!normalizedUuid) {
        return;
      }

      const fullName =
        `${patient.firstName || ""} ${patient.lastName || ""}`.trim();
      nextMap.set(normalizedUuid, fullName || patient.displayName);
    });

    return nextMap;
  }, [patients]);
  const assignedClinicNumberOptions = React.useMemo(() => {
    const uniqueNumbers = new Set<string>();

    assignedPhoneNumbers.forEach((numberItem) => {
      const normalizedNumber = (numberItem.e164 || "").trim();
      if (normalizedNumber) {
        uniqueNumbers.add(normalizedNumber);
      }
    });
    fromNumbers.forEach((numberItem) => {
      const normalizedNumber = (numberItem || "").trim();
      if (normalizedNumber) {
        uniqueNumbers.add(normalizedNumber);
      }
    });

    return Array.from(uniqueNumbers);
  }, [assignedPhoneNumbers, fromNumbers]);
  const assignedClinicNumberSet = React.useMemo(
    () => new Set(assignedClinicNumberOptions),
    [assignedClinicNumberOptions],
  );
  const getPatientNameForCall = React.useCallback(
    (patientUuid?: string | null): string => {
      const normalizedUuid = (patientUuid || "").trim();
      if (!normalizedUuid) {
        return "Unassigned";
      }

      return patientNameByUuid.get(normalizedUuid) || "Unknown Patient";
    },
    [patientNameByUuid],
  );
  const messagingConversations = useMessagingConversations({
    session,
    enabled: isMessagesTab,
    pollIntervalMs: 4000,
  });
  const markMessageConversationRead = messagingConversations.markRead;
  const selectedConversationRecord = React.useMemo(
    () =>
      messagingConversations.conversations.find(
        (conversation) =>
          conversation.conversation_uuid ===
          messagingConversations.selectedConversationUuid,
      ) || null,
    [
      messagingConversations.conversations,
      messagingConversations.selectedConversationUuid,
    ],
  );
  const messageConversations = React.useMemo<TMessageConversationListItem[]>(
    () =>
      toMessageConversationListItems(
        messagingConversations.conversations,
        patientNameByUuid,
        assignedClinicNumberSet,
      ),
    [
      assignedClinicNumberSet,
      messagingConversations.conversations,
      patientNameByUuid,
    ],
  );
  const selectedMessageConversation = React.useMemo(
    () =>
      messageConversations.find(
        (conversation) =>
          conversation.conversationUuid ===
          messagingConversations.selectedConversationUuid,
      ) || null,
    [messageConversations, messagingConversations.selectedConversationUuid],
  );
  const visibleMessageConversations = React.useMemo(() => {
    const includeSaved = selectedMessageSaveStatuses.includes("saved");
    const includeUnsaved = selectedMessageSaveStatuses.includes("unsaved");

    return messageConversations.filter((conversation) => {
      const hasPatient = !!(conversation.patientUuid || "").trim();
      const saveStatusMatches =
        (hasPatient && includeSaved) || (!hasPatient && includeUnsaved);
      const unreadMatches =
        !showUnreadMessagesOnly || (conversation.unreadCount || 0) > 0;
      const assignedNumberMatches =
        !selectedMessageAssignedNumberE164 ||
        (conversation.clinicOwnedE164 || "").trim() ===
          selectedMessageAssignedNumberE164;
      return saveStatusMatches && unreadMatches && assignedNumberMatches;
    });
  }, [
    messageConversations,
    selectedMessageAssignedNumberE164,
    selectedMessageSaveStatuses,
    showUnreadMessagesOnly,
  ]);
  const newMessageToE164 = React.useMemo(
    () => buildDialerE164Number(messageToCountryCode, messageToLocalNumber),
    [messageToCountryCode, messageToLocalNumber],
  );
  const matchedPatientForNewMessage = React.useMemo(() => {
    if (!dialerE164LooksValid(newMessageToE164)) {
      return null;
    }
    return findPatientByPhone(patients, newMessageToE164);
  }, [newMessageToE164, patients]);
  const messageRecipientE164 =
    selectedConversationRecord?.participant_e164 || newMessageToE164;
  const messagePatientUuid =
    selectedConversationRecord?.patient_uuid ||
    matchedPatientForNewMessage?.uuid ||
    null;
  const conversationMessages = useConversationMessages({
    session,
    conversationUuid: selectedConversationRecord?.conversation_uuid || null,
    enabled: isMessagesTab && !!selectedConversationRecord,
    pollIntervalMs: 4000,
  });
  const messageComposer = useMessageComposer({
    session,
    conversationUuid: selectedConversationRecord?.conversation_uuid || null,
    toE164: messageRecipientE164 || null,
    patientUuid: messagePatientUuid,
    fromE164: messageFromE164,
    draftBody: messageComposerDraft,
    onDraftBodyChange: setMessageComposerDraft,
    onOptimisticQueued: (params) => {
      conversationMessages.appendOptimisticOutbound(params);
    },
    onSendSuccess: ({ response, clientRequestId }) => {
      if (selectedConversationRecord) {
        conversationMessages.reconcileOptimisticOutbound({
          clientRequestId,
          messageUuid: response.message_uuid,
          status: response.status,
        });
        conversationMessages.refresh({ silent: true }).catch(() => undefined);
      } else {
        setIsComposingNewMessage(false);
        setMessageToLocalNumber("");
        setMessageToCountryCode(DEFAULT_TO_COUNTRY_CODE);
        messagingConversations.setSelectedConversationUuid(
          response.conversation_uuid,
        );
      }
      messagingConversations.refresh({ silent: true }).catch(() => undefined);
    },
    onSendFailure: ({ clientRequestId, reason }) => {
      if (selectedConversationRecord) {
        conversationMessages.markOptimisticOutboundFailed({
          clientRequestId,
          reason,
        });
      }
    },
  });
  const assignTargetCall = React.useMemo(() => {
    if (assignTargetCallId === null) {
      return null;
    }

    const fromList = calls.find(
      (callItem) => callItem.call_uuid === assignTargetCallId,
    );
    if (fromList) {
      return fromList;
    }

    if (selectedCall?.call_uuid === assignTargetCallId) {
      return selectedCall;
    }

    return null;
  }, [assignTargetCallId, calls, selectedCall]);
  const assignTargetConversation = React.useMemo(() => {
    if (!assignTargetConversationUuid) {
      return null;
    }

    const fromVisibleConversations = messageConversations.find(
      (conversation) =>
        conversation.conversationUuid === assignTargetConversationUuid,
    );
    if (fromVisibleConversations) {
      return fromVisibleConversations;
    }

    if (
      selectedMessageConversation?.conversationUuid ===
      assignTargetConversationUuid
    ) {
      return selectedMessageConversation;
    }

    return null;
  }, [
    assignTargetConversationUuid,
    messageConversations,
    selectedMessageConversation,
  ]);
  const assignPatientOptions = React.useMemo(() => {
    const normalizedTerm = assignPatientSearchTerm.trim();
    if (normalizedTerm) {
      return assignPatientSearch.results;
    }
    return patients.slice(0, 25);
  }, [assignPatientSearch.results, assignPatientSearchTerm, patients]);
  const assignSelectedPatient = React.useMemo(() => {
    if (!assignSelectedPatientUuid) {
      return null;
    }

    return (
      assignPatientOptions.find(
        (patient) => patient.uuid === assignSelectedPatientUuid,
      ) ||
      patients.find((patient) => patient.uuid === assignSelectedPatientUuid) ||
      assignPatientSearch.results.find(
        (patient) => patient.uuid === assignSelectedPatientUuid,
      ) ||
      null
    );
  }, [
    assignPatientOptions,
    assignPatientSearch.results,
    assignSelectedPatientUuid,
    patients,
  ]);
  const toE164 = React.useMemo(
    () => buildDialerE164Number(toCountryCode, toLocalNumber),
    [toCountryCode, toLocalNumber],
  );
  const includedNumberCount = Math.max(
    0,
    communicationsPricing?.included_number_count ?? 1,
  );
  const includedMinutesCount = Math.max(
    0,
    communicationsPricing?.included_minutes ?? DEFAULT_INCLUDED_MINUTES,
  );
  const activeAssignedNumbersCount = assignedPhoneNumbers.length;
  const extraAssignedNumbersCount = Math.max(
    activeAssignedNumbersCount - includedNumberCount,
    0,
  );
  const communicationsUiState: TCommunicationsUiState = activationSubmitting
    ? "communications_activating"
    : activeAssignedNumbersCount <= 0
      ? "communications_not_initialized"
      : activeAssignedNumbersCount > includedNumberCount
        ? "communications_active_with_extra_numbers"
        : "communications_active_with_included_number";
  const communicationsIsActivated =
    communicationsUiState !== "communications_not_initialized" &&
    communicationsUiState !== "communications_activating";
  const filteredAvailablePhoneNumbers = React.useMemo(() => {
    const normalizedSearch = availableNumberSearchTerm.trim().toLowerCase();
    if (!normalizedSearch) {
      return availablePhoneNumbers;
    }

    return availablePhoneNumbers.filter((numberItem) => {
      const numberText =
        `${numberItem.e164 || ""} ${numberItem.friendly_name || ""}`.toLowerCase();
      return numberText.includes(normalizedSearch);
    });
  }, [availableNumberSearchTerm, availablePhoneNumbers]);
  const selectedRoutingPhoneNumber = React.useMemo(
    () =>
      assignedPhoneNumbers.find(
        (phoneNumber) =>
          phoneNumber.phone_number_id === selectedRoutingPhoneNumberId,
      ) || null,
    [assignedPhoneNumbers, selectedRoutingPhoneNumberId],
  );
  const selectableInboundRoutingClinicUsers = React.useMemo(
    () =>
      communicationsAccessUsers.filter(
        (user) => (user.role || "").trim().toUpperCase() !== "PATIENT",
      ),
    [communicationsAccessUsers],
  );
  const totalBillableMinutes = Math.max(
    usageSummary?.total_billable_minutes || 0,
    0,
  );
  const overageMinutes = Math.max(
    usageSummary?.overage_minutes ??
      (totalBillableMinutes > includedMinutesCount
        ? totalBillableMinutes - includedMinutesCount
        : 0),
    0,
  );
  const usageOverageActive = overageMinutes > 0;
  const usageProgressPercent =
    includedMinutesCount > 0
      ? Math.min((totalBillableMinutes / includedMinutesCount) * 100, 100)
      : 0;
  const usagePeriodStartLabel =
    toDateInputFromPeriodValue(usageSummary?.period_start) || usageStartDate;
  const usagePeriodEndLabel =
    toDateInputFromPeriodValue(usageSummary?.period_end) || usageEndDate;

  const refreshCallsList = React.useCallback(async () => {
    if (!session) return;

    setRefreshingCalls(true);
    setCallsPaginationError(null);
    try {
      const requestedLimit = Math.max(calls.length || 0, CALLS_PAGE_SIZE);
      const callsResponse = await listCalls(session, {
        limit: requestedLimit,
        offset: 0,
      });
      setCalls(callsResponse.results || []);
      setCallsTotal(
        typeof callsResponse.total === "number"
          ? callsResponse.total
          : (callsResponse.results || []).length,
      );
    } finally {
      setRefreshingCalls(false);
    }
  }, [calls.length, session]);

  const hasMoreCalls = calls.length < callsTotal;
  const visibleCalls = React.useMemo(() => {
    if (!selectedCallAssignedNumberE164) {
      return calls;
    }

    return calls.filter(
      (call) =>
        resolveCallClinicOwnedE164(call, assignedClinicNumberSet) ===
        selectedCallAssignedNumberE164,
    );
  }, [assignedClinicNumberSet, calls, selectedCallAssignedNumberE164]);

  const loadMoreCalls = React.useCallback(async () => {
    if (!session || loadingMoreCalls || refreshingCalls || !hasMoreCalls) {
      return;
    }

    setLoadingMoreCalls(true);
    setCallsPaginationError(null);

    try {
      const callsResponse = await listCalls(session, {
        limit: CALLS_PAGE_SIZE,
        offset: calls.length,
      });
      const incomingCalls = callsResponse.results || [];

      setCalls((currentCalls) => {
        if (!incomingCalls.length) {
          return currentCalls;
        }

        const existingIds = new Set(
          currentCalls.map((callItem) => callItem.call_uuid),
        );
        const dedupedIncomingCalls = incomingCalls.filter(
          (callItem) => !existingIds.has(callItem.call_uuid),
        );
        if (!dedupedIncomingCalls.length) {
          return currentCalls;
        }
        return [...currentCalls, ...dedupedIncomingCalls];
      });
      setCallsTotal(
        typeof callsResponse.total === "number"
          ? callsResponse.total
          : calls.length + incomingCalls.length,
      );
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to load more calls.";
      setCallsPaginationError(message);
    } finally {
      setLoadingMoreCalls(false);
    }
  }, [calls.length, hasMoreCalls, loadingMoreCalls, refreshingCalls, session]);

  const refreshFromNumbers = React.useCallback(async () => {
    if (!session) return;

    const fromNumbersResponse = await getOutboundFromNumbers(session);
    const nextFromNumbers = fromNumbersResponse.from_numbers || [];
    setFromNumbers(nextFromNumbers);
    setFromE164((current) => {
      return resolvePreferredFromE164({
        current,
        fromNumbers: nextFromNumbers,
        defaultFromE164: fromNumbersResponse.default_from_e164,
      });
    });
    setMessageFromE164((current) => {
      return resolvePreferredFromE164({
        current,
        fromNumbers: nextFromNumbers,
        defaultFromE164: fromNumbersResponse.default_from_e164,
      });
    });
  }, [session]);

  const trackCommunicationsEvent = React.useCallback(
    (eventName: string, properties?: Record<string, unknown>) => {
      mixpanel.track(eventName, {
        ...(session?.clinicId
          ? {
              "Clinic Id": session.clinicId,
            }
          : {}),
        ...(session?.clinicName
          ? {
              "Clinic Name": session.clinicName,
            }
          : {}),
        ...(properties || {}),
      });
    },
    [session?.clinicId, session?.clinicName],
  );

  const loadCommunicationsPricing = React.useCallback(async () => {
    if (!session) return;

    setCommunicationsPricingLoading(true);
    setCommunicationsPricingError(null);

    try {
      const pricingResponse = await getPhoneNumberPricing(session);
      const parsedPricing = parsePricingPayload(pricingResponse);

      if (!parsedPricing) {
        setCommunicationsPricing(null);
        setCommunicationsPricingError(
          "Pricing data is unavailable right now. Please retry.",
        );
        return;
      }

      setCommunicationsPricing(parsedPricing);
    } catch (error) {
      setCommunicationsPricing(null);
      setCommunicationsPricingError(
        toUserFacingErrorMessage(
          error,
          "Unable to load communications pricing right now.",
        ),
      );
    } finally {
      setCommunicationsPricingLoading(false);
    }
  }, [session]);

  const loadAssignedPhoneNumbers = React.useCallback(async () => {
    if (!session) return;

    setAssignedNumbersLoading(true);
    setAssignedNumbersError(null);

    try {
      const assignedResponse = await listAssignedPhoneNumbers(session);
      const normalizedAssignedNumbers =
        parseAssignedPhoneNumbersPayload(assignedResponse);
      setAssignedPhoneNumbers(normalizedAssignedNumbers);
    } catch (error) {
      setAssignedPhoneNumbers([]);
      setAssignedNumbersError(
        toUserFacingErrorMessage(
          error,
          "Unable to load assigned phone numbers right now.",
        ),
      );
    } finally {
      setAssignedNumbersLoading(false);
    }
  }, [session]);

  const loadAvailablePhoneNumbers = React.useCallback(async () => {
    if (!session) return;

    setAvailableNumbersLoading(true);
    setAvailableNumbersError(null);

    try {
      const availableResponse = await listAvailablePhoneNumbers(session);
      const normalizedAvailableNumbers =
        parseAvailablePhoneNumbersPayload(availableResponse);
      setAvailablePhoneNumbers(normalizedAvailableNumbers);
    } catch (error) {
      setAvailablePhoneNumbers([]);
      setAvailableNumbersError(
        toUserFacingErrorMessage(
          error,
          "Unable to load available phone numbers right now.",
        ),
      );
    } finally {
      setAvailableNumbersLoading(false);
    }
  }, [session]);

  const loadCommunicationsAccessUsers = React.useCallback(async () => {
    if (!session || !isClinicAdmin) {
      return;
    }

    setCommunicationsAccessLoading(true);
    setCommunicationsAccessError(null);

    try {
      const accessResponse = await listCommunicationsClinicUsersAccess(session);
      const normalizedUsers =
        parseApiList<TCommunicationsClinicUserAccess>(accessResponse);
      setCommunicationsAccessUsers(normalizedUsers);
      setCommunicationsAccessTotal(
        Number.isFinite(Number(accessResponse.total))
          ? Number(accessResponse.total)
          : normalizedUsers.length,
      );
      setCommunicationsAccessBillableSeatCount(
        Number.isFinite(Number(accessResponse.billable_seat_count))
          ? Number(accessResponse.billable_seat_count)
          : 0,
      );
    } catch (error) {
      setCommunicationsAccessUsers([]);
      setCommunicationsAccessTotal(0);
      setCommunicationsAccessBillableSeatCount(0);
      setCommunicationsAccessError(
        toUserFacingErrorMessage(
          error,
          "Unable to load communications access right now.",
        ),
      );
    } finally {
      setCommunicationsAccessLoading(false);
    }
  }, [isClinicAdmin, session]);

  const resetInboundRoutingForm = React.useCallback(() => {
    setInboundRoutingEnabled(false);
    setInboundRoutingMode("phone_only");
    setInboundRoutingPhoneTargetCountryCode(DEFAULT_TO_COUNTRY_CODE);
    setInboundRoutingPhoneTargetLocalNumber("");
    setInboundRoutingClinicUserUuid("");
    setInboundRoutingTimeoutSeconds(
      String(DEFAULT_INBOUND_ROUTING_TIMEOUT_SECONDS),
    );
    setInboundRoutingFieldErrors({});
  }, []);

  const applyInboundRoutingConfigToForm = React.useCallback(
    (config: TInboundVoiceRoutingConfiguration) => {
      const activeMembers = (config.members || []).filter(
        (member) => member.active !== false,
      );
      const phoneMember = activeMembers.find(
        (member) => member.target_type === "phone_e164",
      );
      const clinicUserMember = activeMembers.find(
        (member) => member.target_type === "clinic_user",
      );
      const splitPhoneTarget = splitDialerE164Number(
        (phoneMember?.target_value || "").trim(),
        DEFAULT_TO_COUNTRY_CODE,
      );

      setInboundRoutingEnabled(Boolean(config.enabled));
      setInboundRoutingMode(resolveInboundRoutingModeFromConfig(config));
      setInboundRoutingPhoneTargetCountryCode(splitPhoneTarget.countryCode);
      setInboundRoutingPhoneTargetLocalNumber(splitPhoneTarget.localNumber);
      setInboundRoutingClinicUserUuid(
        (clinicUserMember?.target_value || "").trim(),
      );
      setInboundRoutingTimeoutSeconds(
        toInboundRoutingTimeoutInput(Number(config.timeout_seconds)),
      );
      setInboundRoutingFieldErrors({});
    },
    [],
  );

  const loadInboundRoutingForPhoneNumber = React.useCallback(
    async (phoneNumberId: number) => {
      if (!session || !phoneNumberId) {
        return;
      }

      setInboundRoutingLoading(true);
      setInboundRoutingError(null);
      setInboundRoutingFieldErrors({});

      try {
        const configResponse = await getInboundVoiceRouting(
          session,
          phoneNumberId,
        );
        applyInboundRoutingConfigToForm(configResponse);
      } catch (error) {
        if (getHttpErrorStatus(error) === 404) {
          resetInboundRoutingForm();
          setInboundRoutingError(null);
          return;
        }

        setInboundRoutingError(
          toUserFacingErrorMessage(
            error,
            "Unable to load inbound routing for this phone number.",
          ),
        );
      } finally {
        setInboundRoutingLoading(false);
      }
    },
    [applyInboundRoutingConfigToForm, resetInboundRoutingForm, session],
  );

  const refreshCommunicationsNumberState = React.useCallback(async () => {
    await Promise.all([
      loadAssignedPhoneNumbers(),
      loadAvailablePhoneNumbers(),
      refreshFromNumbers(),
    ]);
  }, [loadAssignedPhoneNumbers, loadAvailablePhoneNumbers, refreshFromNumbers]);
  const { refreshAfterNumberMutation } = useCommunicationsMutationRefresh({
    refreshCommunicationsNumberState,
    loadUsageSummary,
    refreshCallsList,
    usageStartDate,
    usageEndDate,
  });

  const resolveRedialFromNumber = React.useCallback(
    (requestedFromE164: string | null | undefined): string => {
      const requestedFrom = (requestedFromE164 || "").trim();
      if (requestedFrom && fromNumbers.includes(requestedFrom)) {
        return requestedFrom;
      }

      return (fromE164 || fromNumbers[0] || requestedFrom).trim();
    },
    [fromE164, fromNumbers],
  );

  const handlePatientSearchTermChange = React.useCallback(
    (term: string) => {
      setPatientSearchTerm(term);

      setPatientUuid((currentSelectedUuid) => {
        if (!currentSelectedUuid) {
          return "";
        }

        const currentSelectedPatient = patients.find(
          (patient) => patient.uuid === currentSelectedUuid,
        );
        if (!currentSelectedPatient) {
          return "";
        }

        if (term.trim() === currentSelectedPatient.displayName) {
          return currentSelectedUuid;
        }

        return "";
      });
    },
    [patients],
  );

  const handleClearPatientSearch = React.useCallback(() => {
    setPatientSearchTerm("");
    setPatientUuid("");
  }, []);

  const setDialerNumberFromE164 = React.useCallback(
    (value: string) => {
      const splitNumber = splitDialerE164Number(value, toCountryCode);
      setToCountryCode(splitNumber.countryCode);
      setToLocalNumber(splitNumber.localNumber);
    },
    [toCountryCode],
  );

  const handleToCountryCodeChange = React.useCallback((value: string) => {
    setToCountryCode(normalizeDialerCountryCode(value));
  }, []);

  const handleToLocalNumberChange = React.useCallback(
    (value: string) => {
      const normalizedValue = (value || "").trim();
      if (normalizedValue.startsWith("+")) {
        setDialerNumberFromE164(normalizedValue);
        return;
      }

      setToLocalNumber(normalizeDialerLocalNumber(value));
    },
    [setDialerNumberFromE164],
  );

  const handleSelectPatient = React.useCallback(
    (patient: TPatientOption) => {
      const patientPhone = normalizePatientPhoneForCall(patient.phone);

      setPatientSearchTerm(patient.displayName);
      setPatientUuid(patient.uuid);

      if (!patientPhone) {
        setToLocalNumber("");
        return;
      }

      setDialerNumberFromE164(patientPhone);
    },
    [setDialerNumberFromE164],
  );

  const handleActivateCommunications = React.useCallback(async () => {
    if (!session || activationSubmitting) {
      return;
    }

    const firstAvailableNumber = availablePhoneNumbers[0];
    if (!firstAvailableNumber) {
      setCommunicationsActionFeedback({
        type: "error",
        message:
          "No inventory is available right now. Please retry in a few minutes.",
      });
      return;
    }

    trackCommunicationsEvent("Communications FTUI Activation CTA Clicked");
    trackCommunicationsEvent("Communications Setup Start", {
      "Phone Number ID": firstAvailableNumber.phone_number_id,
    });
    setActivationSubmitting(true);
    setCommunicationsActionFeedback(null);

    try {
      const claimResponse = await claimAvailablePhoneNumber(
        session,
        firstAvailableNumber.phone_number_id,
        {
          make_primary: true,
        },
      );

      await refreshAfterNumberMutation();

      const resolvedNumber =
        (claimResponse.e164 || "").trim() || firstAvailableNumber.e164;
      const formattedResolvedNumber = formatPhoneNumberDisplay(resolvedNumber, {
        fallback: resolvedNumber || "number",
      });
      setCommunicationsActionFeedback({
        type: "success",
        message: `Communications is active. Assigned ${formattedResolvedNumber}.`,
      });
      trackCommunicationsEvent("Communications Activation Succeeded", {
        "Phone Number ID": firstAvailableNumber.phone_number_id,
        E164: resolvedNumber,
      });
      trackCommunicationsEvent("Communications Setup Success", {
        "Phone Number ID": firstAvailableNumber.phone_number_id,
      });
    } catch (error) {
      const statusCode = getHttpErrorStatus(error);
      const message =
        statusCode === 409
          ? "This number was just claimed by another clinic. Please retry."
          : toUserFacingErrorMessage(
              error,
              "Failed to activate communications right now.",
            );
      setCommunicationsActionFeedback({
        type: "error",
        message,
      });
      trackCommunicationsEvent("Communications Activation Failed", {
        Status: statusCode || "unknown",
      });
      trackCommunicationsEvent("Communications Setup Failure", {
        Status: statusCode || "unknown",
      });
      await refreshCommunicationsNumberState();
    } finally {
      setActivationSubmitting(false);
    }
  }, [
    activationSubmitting,
    availablePhoneNumbers,
    refreshAfterNumberMutation,
    refreshCommunicationsNumberState,
    session,
    trackCommunicationsEvent,
  ]);

  const handleClaimAdditionalNumber = React.useCallback(
    async (phoneNumber: TCommunicationsPhoneNumberInventoryItem) => {
      if (!session || claimingPhoneNumberId !== null) {
        return;
      }

      trackCommunicationsEvent("Communications Claim Start", {
        "Phone Number ID": phoneNumber.phone_number_id,
      });
      setClaimingPhoneNumberId(phoneNumber.phone_number_id);
      setCommunicationsActionFeedback(null);

      try {
        const claimResponse = await claimAvailablePhoneNumber(
          session,
          phoneNumber.phone_number_id,
        );
        await refreshAfterNumberMutation();
        const claimedNumber = claimResponse.e164 || phoneNumber.e164;
        setCommunicationsActionFeedback({
          type: "success",
          message: `Claimed ${formatPhoneNumberDisplay(claimedNumber, {
            fallback: claimedNumber || "number",
          })}.`,
        });
        trackCommunicationsEvent("Communications Claim Success", {
          "Phone Number ID": phoneNumber.phone_number_id,
          E164: claimResponse.e164 || phoneNumber.e164,
        });
      } catch (error) {
        const statusCode = getHttpErrorStatus(error);
        const message =
          statusCode === 409
            ? "That number was just claimed. Refreshing inventory now."
            : toUserFacingErrorMessage(error, "Failed to claim this number.");
        setCommunicationsActionFeedback({
          type: "error",
          message,
        });
        trackCommunicationsEvent("Communications Claim Failure", {
          "Phone Number ID": phoneNumber.phone_number_id,
          Status: statusCode || "unknown",
        });
        await refreshCommunicationsNumberState();
      } finally {
        setClaimingPhoneNumberId(null);
      }
    },
    [
      claimingPhoneNumberId,
      refreshAfterNumberMutation,
      refreshCommunicationsNumberState,
      session,
      trackCommunicationsEvent,
    ],
  );

  const handleSetPrimaryNumber = React.useCallback(
    async (phoneNumber: TCommunicationsAssignedPhoneNumberItem) => {
      if (
        !session ||
        updatingPrimaryNumberId !== null ||
        phoneNumber.is_primary === true
      ) {
        return;
      }

      setUpdatingPrimaryNumberId(phoneNumber.phone_number_id);
      setCommunicationsActionFeedback(null);

      try {
        await setPrimaryPhoneNumber(session, phoneNumber.phone_number_id);
        await refreshCommunicationsNumberState();
        const formattedPrimaryNumber = formatPhoneNumberDisplay(
          phoneNumber.e164,
          { fallback: "number" },
        );
        setCommunicationsActionFeedback({
          type: "success",
          message: `Set ${formattedPrimaryNumber} as primary.`,
        });
        trackCommunicationsEvent("Communications Primary Change Success", {
          "Phone Number ID": phoneNumber.phone_number_id,
        });
      } catch (error) {
        const statusCode = getHttpErrorStatus(error);
        setCommunicationsActionFeedback({
          type: "error",
          message: toUserFacingErrorMessage(
            error,
            "Failed to update the primary number.",
          ),
        });
        trackCommunicationsEvent("Communications Primary Change Failure", {
          "Phone Number ID": phoneNumber.phone_number_id,
          Status: statusCode || "unknown",
        });
      } finally {
        setUpdatingPrimaryNumberId(null);
      }
    },
    [
      refreshCommunicationsNumberState,
      session,
      trackCommunicationsEvent,
      updatingPrimaryNumberId,
    ],
  );

  const handleUnassignNumber = React.useCallback(
    async (phoneNumber: TCommunicationsAssignedPhoneNumberItem) => {
      if (!session || unassigningPhoneNumberId !== null) {
        return;
      }
      const phoneNumberId = Number(phoneNumber.phone_number_id);
      if (!Number.isFinite(phoneNumberId)) {
        setCommunicationsActionFeedback({
          type: "error",
          message: "This number is missing an ID and cannot be unassigned.",
        });
        trackCommunicationsEvent("Communications Unassign Failure", {
          "Phone Number ID": "invalid",
          Status: "invalid_phone_number_id",
        });
        return;
      }

      if (
        Platform.OS === "web" &&
        typeof window !== "undefined" &&
        !window.confirm(
          `Unassign ${formatPhoneNumberDisplay(phoneNumber.e164, { fallback: "this number" })} from this clinic?`,
        )
      ) {
        return;
      }

      setUnassigningPhoneNumberId(phoneNumberId);
      setCommunicationsActionFeedback(null);

      try {
        await unassignPhoneNumber(session, phoneNumberId);
        await refreshAfterNumberMutation();
        const formattedUnassignedNumber = formatPhoneNumberDisplay(
          phoneNumber.e164,
          { fallback: "number" },
        );
        setCommunicationsActionFeedback({
          type: "success",
          message: `Unassigned ${formattedUnassignedNumber}.`,
        });
        trackCommunicationsEvent("Communications Unassign Success", {
          "Phone Number ID": phoneNumberId,
        });
      } catch (error) {
        const statusCode = getHttpErrorStatus(error);
        setCommunicationsActionFeedback({
          type: "error",
          message: toUserFacingErrorMessage(
            error,
            "Failed to unassign that number.",
          ),
        });
        trackCommunicationsEvent("Communications Unassign Failure", {
          "Phone Number ID": phoneNumberId,
          Status: statusCode || "unknown",
        });
      } finally {
        setUnassigningPhoneNumberId(null);
      }
    },
    [
      refreshAfterNumberMutation,
      refreshCommunicationsNumberState,
      session,
      trackCommunicationsEvent,
      unassigningPhoneNumberId,
    ],
  );

  const handleToggleCommunicationsAccess = React.useCallback(
    async (user: TCommunicationsClinicUserAccess) => {
      if (
        !session ||
        !isClinicAdmin ||
        mutatingCommunicationsAccessUserUuid !== null ||
        user.role === "PATIENT"
      ) {
        return;
      }

      setMutatingCommunicationsAccessUserUuid(user.user_uuid);
      setCommunicationsAccessActionFeedback(null);
      setCommunicationsAccessError(null);

      try {
        const updateResponse = user.communications_enabled
          ? await disableCommunicationsClinicUserAccess(session, user.user_uuid)
          : await enableCommunicationsClinicUserAccess(session, user.user_uuid);

        setCommunicationsAccessUsers((currentUsers) =>
          currentUsers.map((currentUser) =>
            currentUser.user_uuid === user.user_uuid
              ? updateResponse.user
              : currentUser,
          ),
        );
        setCommunicationsAccessBillableSeatCount(
          Number.isFinite(Number(updateResponse.billable_seat_count))
            ? Number(updateResponse.billable_seat_count)
            : 0,
        );
        setCommunicationsAccessActionFeedback({
          type: "success",
          message: user.communications_enabled
            ? "Communications access disabled."
            : "Communications access enabled.",
        });
        const reloadResult = await Promise.allSettled([
          loadCommunicationsAccessUsers(),
        ]);
        if (reloadResult[0]?.status === "rejected") {
          setCommunicationsAccessError(
            "Access updated, but the latest list could not be refreshed.",
          );
        }
      } catch (error) {
        setCommunicationsAccessActionFeedback({
          type: "error",
          message: toUserFacingErrorMessage(
            error,
            "Unable to update communications access right now.",
          ),
        });
      } finally {
        setMutatingCommunicationsAccessUserUuid(null);
      }
    },
    [
      isClinicAdmin,
      loadCommunicationsAccessUsers,
      mutatingCommunicationsAccessUserUuid,
      session,
    ],
  );

  const handleInboundRoutingEnabledChange = React.useCallback(
    (nextEnabled: boolean) => {
      setInboundRoutingEnabled(nextEnabled);
      setInboundRoutingFieldErrors((currentErrors) => ({
        ...currentErrors,
        save: undefined,
      }));
    },
    [],
  );

  const handleInboundRoutingModeChange = React.useCallback(
    (nextMode: TInboundRoutingMode) => {
      setInboundRoutingMode(nextMode);
      setInboundRoutingFieldErrors({});
    },
    [],
  );

  const handleInboundRoutingPhoneTargetCountryCodeChange = React.useCallback(
    (value: string) => {
      setInboundRoutingPhoneTargetCountryCode(
        normalizeDialerCountryCode(value),
      );
      setInboundRoutingFieldErrors((currentErrors) => ({
        ...currentErrors,
        phoneTarget: undefined,
        save: undefined,
      }));
    },
    [],
  );

  const handleInboundRoutingPhoneTargetLocalNumberChange = React.useCallback(
    (value: string) => {
      setInboundRoutingPhoneTargetLocalNumber(
        normalizeDialerLocalNumber(value),
      );
      setInboundRoutingFieldErrors((currentErrors) => ({
        ...currentErrors,
        phoneTarget: undefined,
        save: undefined,
      }));
    },
    [],
  );

  const handleInboundRoutingClinicUserChange = React.useCallback(
    (value: string) => {
      setInboundRoutingClinicUserUuid((value || "").trim());
      setInboundRoutingFieldErrors((currentErrors) => ({
        ...currentErrors,
        clinicUserUuid: undefined,
        save: undefined,
      }));
    },
    [],
  );

  const handleInboundRoutingTimeoutChange = React.useCallback(
    (value: string) => {
      setInboundRoutingTimeoutSeconds(normalizeTimeoutSecondsInput(value));
      setInboundRoutingFieldErrors((currentErrors) => ({
        ...currentErrors,
        timeoutSeconds: undefined,
        save: undefined,
      }));
    },
    [],
  );

  const handleRetryInboundRoutingLoad = React.useCallback(() => {
    if (!selectedRoutingPhoneNumberId) {
      return;
    }

    loadInboundRoutingForPhoneNumber(selectedRoutingPhoneNumberId).catch(
      () => undefined,
    );
  }, [loadInboundRoutingForPhoneNumber, selectedRoutingPhoneNumberId]);

  const handleSaveInboundRouting = React.useCallback(async () => {
    if (
      !session ||
      !isClinicAdmin ||
      !selectedRoutingPhoneNumberId ||
      inboundRoutingSaving
    ) {
      return;
    }

    const normalizedPhoneTarget = buildDialerE164Number(
      inboundRoutingPhoneTargetCountryCode,
      inboundRoutingPhoneTargetLocalNumber,
    );
    const normalizedClinicUserUuid = inboundRoutingClinicUserUuid.trim();
    const normalizedTimeoutInput = normalizeTimeoutSecondsInput(
      inboundRoutingTimeoutSeconds,
    );
    const parsedTimeoutSeconds = Number.parseInt(normalizedTimeoutInput, 10);

    const phoneTargetRequired =
      inboundRoutingMode === "phone_only" ||
      inboundRoutingMode === "simultaneous";
    const clinicUserRequired =
      inboundRoutingMode === "webapp_only" ||
      inboundRoutingMode === "simultaneous";

    const validationErrors: TInboundRoutingFieldErrors = {};
    if (
      !Number.isFinite(parsedTimeoutSeconds) ||
      parsedTimeoutSeconds < 1 ||
      parsedTimeoutSeconds > 120
    ) {
      validationErrors.timeoutSeconds =
        "Enter a timeout between 1 and 120 seconds.";
    }
    if (
      inboundRoutingEnabled &&
      phoneTargetRequired &&
      !dialerE164LooksValid(normalizedPhoneTarget)
    ) {
      validationErrors.phoneTarget =
        "Enter a valid E.164 number, for example +14155550123.";
    }
    if (
      inboundRoutingEnabled &&
      clinicUserRequired &&
      !normalizedClinicUserUuid
    ) {
      validationErrors.clinicUserUuid =
        "Select a clinic user for webapp inbound routing.";
    }

    if (Object.values(validationErrors).some(Boolean)) {
      setInboundRoutingFieldErrors(validationErrors);
      return;
    }

    const strategy =
      inboundRoutingMode === "simultaneous" ? "simultaneous" : "sequential";
    const members: TInboundVoiceRoutingConfiguration["members"] = [];
    if (phoneTargetRequired && normalizedPhoneTarget) {
      members.push({
        target_type: "phone_e164",
        target_value: normalizedPhoneTarget,
        priority: 0,
        weight: 1,
        active: true,
      });
    }
    if (clinicUserRequired && normalizedClinicUserUuid) {
      members.push({
        target_type: "clinic_user",
        target_value: normalizedClinicUserUuid,
        priority: members.length,
        weight: 1,
        active: true,
      });
    }

    setInboundRoutingSaving(true);
    setInboundRoutingError(null);
    setInboundRoutingFieldErrors({});

    try {
      if (
        inboundRoutingEnabled &&
        clinicUserRequired &&
        normalizedClinicUserUuid
      ) {
        const accessResponse = await enableCommunicationsClinicUserAccess(
          session,
          normalizedClinicUserUuid,
        );
        setCommunicationsAccessUsers((currentUsers) => {
          const hasUser = currentUsers.some(
            (user) => user.user_uuid === accessResponse.user.user_uuid,
          );
          if (hasUser) {
            return currentUsers.map((currentUser) =>
              currentUser.user_uuid === accessResponse.user.user_uuid
                ? accessResponse.user
                : currentUser,
            );
          }
          return [...currentUsers, accessResponse.user];
        });
        setCommunicationsAccessBillableSeatCount(
          Number.isFinite(Number(accessResponse.billable_seat_count))
            ? Number(accessResponse.billable_seat_count)
            : 0,
        );
      }

      await updateInboundVoiceRouting(session, selectedRoutingPhoneNumberId, {
        enabled: inboundRoutingEnabled,
        strategy,
        timeout_seconds: parsedTimeoutSeconds,
        members,
      });

      await loadInboundRoutingForPhoneNumber(selectedRoutingPhoneNumberId);

      setCommunicationsActionFeedback({
        type: "success",
        message: `Inbound routing saved for ${formatPhoneNumberDisplay(
          selectedRoutingPhoneNumber?.e164,
          {
            fallback: "the selected number",
          },
        )}.`,
      });
    } catch (error) {
      setInboundRoutingFieldErrors({
        save: toUserFacingErrorMessage(
          error,
          "Unable to save inbound call routing right now.",
        ),
      });
    } finally {
      setInboundRoutingSaving(false);
    }
  }, [
    inboundRoutingClinicUserUuid,
    inboundRoutingEnabled,
    inboundRoutingMode,
    inboundRoutingPhoneTargetCountryCode,
    inboundRoutingPhoneTargetLocalNumber,
    inboundRoutingSaving,
    inboundRoutingTimeoutSeconds,
    isClinicAdmin,
    loadInboundRoutingForPhoneNumber,
    selectedRoutingPhoneNumberId,
    session,
  ]);

  const handleUsageDateApply = React.useCallback(() => {
    loadUsageSummary(usageStartDate, usageEndDate).catch(() => undefined);
  }, [loadUsageSummary, usageEndDate, usageStartDate]);

  const openAssignPatientModal = React.useCallback(
    (call: TCommunicationsCall) => {
      if (assignSubmitting) {
        return;
      }

      const normalizedPatientUuid = (call.patient_uuid || "").trim();
      setSelectedCallId(call.call_uuid);
      setAssignTargetCallId(call.call_uuid);
      setAssignTargetConversationUuid(null);
      setAssignPatientSearchTerm("");
      setAssignSelectedPatientUuid(normalizedPatientUuid);
      setAssignFeedback(null);
      setCallDetailError(null);
      setAssignModalVisible(true);
    },
    [assignSubmitting],
  );

  const openAssignConversationPatientModal = React.useCallback(
    (conversation: TMessageConversationListItem) => {
      if (assignSubmitting) {
        return;
      }

      const normalizedPatientUuid = (conversation.patientUuid || "").trim();
      setAssignTargetCallId(null);
      setAssignTargetConversationUuid(conversation.conversationUuid);
      setAssignPatientSearchTerm("");
      setAssignSelectedPatientUuid(normalizedPatientUuid);
      setAssignFeedback(null);
      setCallDetailError(null);
      setAssignModalVisible(true);
    },
    [assignSubmitting],
  );

  const closeAssignPatientModal = React.useCallback(() => {
    if (assignSubmitting) {
      return;
    }

    setAssignModalVisible(false);
  }, [assignSubmitting]);

  React.useEffect(() => {
    if (assignModalVisible) {
      return;
    }

    setAssignTargetCallId(null);
    setAssignTargetConversationUuid(null);
    setAssignPatientSearchTerm("");
    setAssignSelectedPatientUuid("");
    setAssignPhoneOverwritePrompt(null);
  }, [assignModalVisible]);

  React.useEffect(() => {
    if (!session) {
      return;
    }

    const normalizedToNumber = toE164.trim();
    if (!normalizedToNumber) {
      return;
    }

    const locallyMatchedPatient = findPatientByPhone(
      patients,
      normalizedToNumber,
    );
    if (locallyMatchedPatient) {
      setPatientUuid(locallyMatchedPatient.uuid);
      return;
    }

    setPatientUuid((currentSelectedUuid) => {
      if (!currentSelectedUuid) {
        return "";
      }

      const currentSelectedPatient = patients.find(
        (patient) => patient.uuid === currentSelectedUuid,
      );
      if (!currentSelectedPatient) {
        return "";
      }

      const selectedPhone = normalizePatientPhoneForCall(
        currentSelectedPatient.phone,
      );
      return phonesLikelyMatch(selectedPhone, normalizedToNumber)
        ? currentSelectedUuid
        : "";
    });

    const toNumberDigits = getPhoneDigits(normalizedToNumber);
    if (toNumberDigits.length < 7) {
      return;
    }

    let isMounted = true;
    const timeoutId = setTimeout(async () => {
      try {
        const results = await getPatients(session, {
          phone: normalizedToNumber,
          useSubstring: true,
          limit: 25,
        });

        if (!isMounted) {
          return;
        }

        const remotePatients = (results || []).map(toPatientOption);
        if (remotePatients.length) {
          setPatients((currentPatients) =>
            mergePatientOptions(currentPatients, remotePatients),
          );
        }

        const remoteMatchedPatient = findPatientByPhone(
          remotePatients,
          normalizedToNumber,
        );
        if (remoteMatchedPatient) {
          setPatientUuid(remoteMatchedPatient.uuid);
        }
      } catch (error) {
        // Keep dialing available even if patient lookup fails.
      }
    }, 300);

    return () => {
      isMounted = false;
      clearTimeout(timeoutId);
    };
  }, [patients, session, toE164]);

  const completeCallAssignment = React.useCallback(
    async (options: {
      callId: TCommunicationsCallId;
      patient: TPatientOption;
      overwriteExistingPhoneNumber?: boolean;
      successMessage: string;
    }) => {
      if (!session) {
        return;
      }

      await assignCallToPatient(session, options.callId, {
        patient_uuid: options.patient.uuid,
        bulk_update: true,
        ...(options.overwriteExistingPhoneNumber !== undefined
          ? {
              overwrite_existing_phone_number:
                options.overwriteExistingPhoneNumber,
            }
          : {}),
      });

      setAssignModalVisible(false);
      setAssignPhoneOverwritePrompt(null);
      setCallDetailError(null);
      setAssignFeedback({
        type: "success",
        message: options.successMessage,
      });
      setSelectedCallId(options.callId);

      await Promise.allSettled([
        refreshCallsList(),
        loadCallDetail(options.callId, { silent: true }),
      ]);
    },
    [loadCallDetail, refreshCallsList, session],
  );

  const completeConversationAssignment = React.useCallback(
    async (options: {
      conversationUuid: string;
      patient: TPatientOption;
      overwriteExistingPhoneNumber?: boolean;
      successMessage: string;
    }) => {
      if (!session) {
        return;
      }

      await assignConversationToPatient(session, options.conversationUuid, {
        patient_uuid: options.patient.uuid,
        bulk_update: true,
        ...(options.overwriteExistingPhoneNumber !== undefined
          ? {
              overwrite_existing_phone_number:
                options.overwriteExistingPhoneNumber,
            }
          : {}),
      });

      setAssignModalVisible(false);
      setAssignPhoneOverwritePrompt(null);
      setCallDetailError(null);
      setAssignFeedback({
        type: "success",
        message: options.successMessage,
      });

      await Promise.allSettled([
        messagingConversations.refresh({ silent: true }),
        conversationMessages.refresh({ silent: true }),
      ]);
    },
    [conversationMessages, messagingConversations, session],
  );

  const getCallAssignmentTimestampLabel = React.useCallback(
    (callId: TCommunicationsCallId): string => {
      const matchingCall =
        calls.find((call) => call.call_uuid === callId) ||
        (selectedCall?.call_uuid === callId ? selectedCall : null);
      const timestampValue =
        matchingCall?.started_at ||
        matchingCall?.answered_at ||
        matchingCall?.created_at ||
        null;
      return timestampValue ? formatDateTime(timestampValue) : "unknown time";
    },
    [calls, selectedCall],
  );

  const handleAssignSelectedPatient = React.useCallback(async () => {
    const hasCallTarget = assignTargetCallId !== null;
    const hasConversationTarget = !!assignTargetConversationUuid;
    if ((!hasCallTarget && !hasConversationTarget) || !assignSelectedPatient) {
      return;
    }
    if (assignSubmitting) {
      return;
    }

    setAssignSubmitting(true);
    setCallDetailError(null);
    setAssignFeedback(null);

    try {
      if (hasCallTarget && assignTargetCallId !== null) {
        const callTimestampLabel =
          getCallAssignmentTimestampLabel(assignTargetCallId);
        await completeCallAssignment({
          callId: assignTargetCallId,
          patient: assignSelectedPatient,
          successMessage: `Assigned call made at ${callTimestampLabel} to ${assignSelectedPatient.displayName}.`,
        });
      } else if (hasConversationTarget && assignTargetConversationUuid) {
        const participantLabel = formatPhoneNumberDisplay(
          assignTargetConversation?.participantE164,
          {
            fallback: "this number",
          },
        );
        await completeConversationAssignment({
          conversationUuid: assignTargetConversationUuid,
          patient: assignSelectedPatient,
          successMessage: `Assigned conversation with ${participantLabel} to ${assignSelectedPatient.displayName}.`,
        });
      }
    } catch (error) {
      if (isAssignPhoneAlreadySetConflict(error)) {
        setAssignPhoneOverwritePrompt({
          target:
            assignTargetCallId !== null
              ? { type: "call", callId: assignTargetCallId }
              : {
                  type: "conversation",
                  conversationUuid: assignTargetConversationUuid || "",
                },
          patient: assignSelectedPatient,
        });
        return;
      }

      const message = toUserFacingErrorMessage(
        error,
        assignTargetCallId !== null
          ? "Failed to assign call to patient."
          : "Failed to assign conversation to patient.",
      );
      setCallDetailError(message);
      setAssignFeedback({
        type: "error",
        message,
      });
    } finally {
      setAssignSubmitting(false);
    }
  }, [
    getCallAssignmentTimestampLabel,
    assignSelectedPatient,
    assignSubmitting,
    assignTargetCallId,
    assignTargetConversation,
    assignTargetConversationUuid,
    completeCallAssignment,
    completeConversationAssignment,
  ]);

  const handleResolveAssignPhoneOverwrite = React.useCallback(
    async (overwriteExistingPhoneNumber: boolean) => {
      if (!assignPhoneOverwritePrompt || assignSubmitting) {
        return;
      }

      const { target, patient } = assignPhoneOverwritePrompt;
      setAssignSubmitting(true);
      setCallDetailError(null);
      setAssignFeedback(null);

      try {
        if (target.type === "call") {
          const callTimestampLabel = getCallAssignmentTimestampLabel(
            target.callId,
          );
          await completeCallAssignment({
            callId: target.callId,
            patient,
            overwriteExistingPhoneNumber,
            successMessage: overwriteExistingPhoneNumber
              ? `Assigned call made at ${callTimestampLabel} to ${patient.displayName} and replaced the patient's phone number with this call number.`
              : `Assigned call made at ${callTimestampLabel} to ${patient.displayName} without changing the patient's phone number.`,
          });
        } else {
          const participantLabel = formatPhoneNumberDisplay(
            assignTargetConversation?.participantE164,
            {
              fallback: "this conversation",
            },
          );
          await completeConversationAssignment({
            conversationUuid: target.conversationUuid,
            patient,
            overwriteExistingPhoneNumber,
            successMessage: overwriteExistingPhoneNumber
              ? `Assigned conversation with ${participantLabel} to ${patient.displayName} and replaced the patient's phone number with this conversation number.`
              : `Assigned conversation with ${participantLabel} to ${patient.displayName} without changing the patient's phone number.`,
          });
        }
      } catch (error) {
        const message = toUserFacingErrorMessage(
          error,
          target.type === "call"
            ? "Failed to assign call to patient."
            : "Failed to assign conversation to patient.",
        );
        setCallDetailError(message);
        setAssignFeedback({
          type: "error",
          message,
        });
      } finally {
        setAssignSubmitting(false);
      }
    },
    [
      assignTargetConversation,
      assignPhoneOverwritePrompt,
      assignSubmitting,
      completeCallAssignment,
      completeConversationAssignment,
      getCallAssignmentTimestampLabel,
    ],
  );

  const handleRequestHangup = React.useCallback(async () => {
    if (!session || selectedCallId === null) return;

    setHangupSubmitting(true);
    setCallDetailError(null);

    try {
      await hangupCall(session, selectedCallId, {
        callAttemptId:
          (callSession.activeCallContext?.callId === selectedCallId
            ? callSession.activeCallContext.clientRequestId
            : null) ||
          selectedCall?.client_request_id ||
          null,
      });

      await Promise.all([
        refreshCallsList(),
        loadCallDetail(selectedCallId, { silent: true }),
      ]);
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to request call hangup.";
      setCallDetailError(message);
    } finally {
      setHangupSubmitting(false);
    }
  }, [
    callSession.activeCallContext?.callId,
    callSession.activeCallContext?.clientRequestId,
    loadCallDetail,
    refreshCallsList,
    selectedCall?.client_request_id,
    selectedCallId,
    session,
  ]);

  const handleCallAgain = React.useCallback(
    (call: TCommunicationsCall) => {
      const nextTo = (call.to_e164 || "").trim();
      const nextFrom = resolveRedialFromNumber(call.from_e164);
      const nextPatientUuid = (call.patient_uuid || "").trim();

      if (!nextTo || !nextFrom || callSession.isStartingCall) {
        return;
      }

      setSelectedCallId(call.call_uuid);
      setDialerNumberFromE164(nextTo);
      setFromE164(nextFrom);
      setPatientUuid(nextPatientUuid);

      callSession
        .startOutboundCall({
          toE164: nextTo,
          fromE164: nextFrom,
          patientUuid: nextPatientUuid || undefined,
        })
        .then(() => {
          refreshCallsList().catch(() => undefined);
        })
        .catch(() => undefined);
    },
    [
      callSession,
      refreshCallsList,
      resolveRedialFromNumber,
      setDialerNumberFromE164,
    ],
  );

  const handleToggleCallNoteSummary = React.useCallback((noteId: string) => {
    setExpandedCallNoteSummaries((currentSummaries) => ({
      ...currentSummaries,
      [noteId]: !currentSummaries[noteId],
    }));
  }, []);

  React.useEffect(() => {
    const loadInitialData = async () => {
      if (!session) return;

      setInitialLoading(true);
      setPageError(null);

      try {
        const [fromNumbersResponse, callsResponse, patientsList] =
          await Promise.all([
            getOutboundFromNumbers(session),
            listCalls(session, { limit: CALLS_PAGE_SIZE, offset: 0 }),
            getPatients(session, { limit: 50, offset: 0, useSubstring: false }),
          ]);

        const initialFromNumbers = fromNumbersResponse.from_numbers || [];
        const initialFromE164 = resolvePreferredFromE164({
          current: "",
          fromNumbers: initialFromNumbers,
          defaultFromE164: fromNumbersResponse.default_from_e164,
        });
        setFromNumbers(initialFromNumbers);
        setFromE164(initialFromE164);
        setMessageFromE164(initialFromE164);

        setCalls(callsResponse.results || []);
        setCallsTotal(
          typeof callsResponse.total === "number"
            ? callsResponse.total
            : (callsResponse.results || []).length,
        );
        setPatients((patientsList || []).map(toPatientOption));

        const [organizedAudioResponse, organizedNotesResponse] =
          await Promise.allSettled([
            getOrganizedClinicAudioForCalls(session),
            getOrganizedClinicNotesForCalls(session),
          ]);

        if (organizedAudioResponse.status === "fulfilled") {
          setOrganizedAudioSummary({
            totalPatients: organizedAudioResponse.value.total_patients,
            totalAudioFiles: organizedAudioResponse.value.total_audio_files,
            uncategorizedCount:
              organizedAudioResponse.value.uncategorized_count,
          });
        }

        if (organizedNotesResponse.status === "fulfilled") {
          setOrganizedNotesSummary({
            totalPatients: organizedNotesResponse.value.total_patients,
            totalNotes: organizedNotesResponse.value.total_notes,
            uncategorizedCount:
              organizedNotesResponse.value.uncategorized_count,
          });
        }
      } catch (error) {
        const message =
          error instanceof Error
            ? error.message
            : "Failed to load calls dashboard data.";
        setPageError(message);
      } finally {
        setInitialLoading(false);
      }
    };

    loadInitialData();
  }, [session]);

  React.useEffect(() => {
    if (selectedCallId === null) return;
    setReprocessingRecordingId(null);
    setExpandedCallNoteSummaries({});
    loadCallDetail(selectedCallId);
  }, [selectedCallId, loadCallDetail]);

  const findDocumentationAudioIdForRecording = React.useCallback(
    (
      recording: TCommunicationsRecording,
      options?: {
        callNotes?: TCommunicationsCallNote[];
        patientAudioMetadata?: TDocumentationAudioMetadata[];
      },
    ): number | null => {
      const callNotes = options?.callNotes || selectedCallNotes;
      const patientAudioMetadata =
        options?.patientAudioMetadata || patientAudio;

      for (const note of callNotes) {
        for (const audioInfo of note.audio_info || []) {
          if (
            recordingMatchesAudioSource(recording, {
              s3Key: audioInfo.s3_key,
              getUrl: audioInfo.get_url,
            })
          ) {
            return audioInfo.id;
          }
        }
      }

      for (const audioItem of patientAudioMetadata) {
        if (
          recordingMatchesAudioSource(recording, {
            s3Key: audioItem.s3_key,
            getUrl: audioItem.get_url,
          })
        ) {
          return audioItem.id;
        }
      }

      return null;
    },
    [patientAudio, selectedCallNotes],
  );

  const handleReprocessRecording = React.useCallback(
    async (recording: TCommunicationsRecording) => {
      if (!session || !selectedCall) {
        return;
      }

      const patientUuid = (selectedCall.patient_uuid || "").trim();
      if (!patientUuid) {
        Alert.alert(
          "Unable to Reprocess",
          "This call is not linked to a patient yet.",
        );
        return;
      }

      setReprocessingRecordingId(recording.id);
      setCallDetailError(null);

      try {
        let documentationAudioId =
          findDocumentationAudioIdForRecording(recording);

        if (!documentationAudioId) {
          const audioResponse = await getPatientAudioForCallDocs(
            session,
            patientUuid,
            true,
          );
          const refreshedAudio = (audioResponse.metadata || []).filter(
            (audioItem) => audioItem.type === "phone_call_audio",
          );
          setPatientAudio(refreshedAudio);

          documentationAudioId = findDocumentationAudioIdForRecording(
            recording,
            {
              patientAudioMetadata: refreshedAudio,
            },
          );
        }

        if (!documentationAudioId) {
          throw new Error(
            "Could not locate a linked audio file for this recording.",
          );
        }

        const result = await reprocessAudioAsDifferentNoteType({
          session,
          audioId: documentationAudioId,
          patientId: patientUuid,
          encounterType: "Phone Call",
        });

        if (result.status === "completed") {
          Alert.alert(
            "Reprocess Complete",
            "Audio was reprocessed as Phone Call and a new note was created.",
          );
        } else {
          Alert.alert(
            "Still Processing",
            "Reprocessing started. The new note should appear shortly.",
          );
        }

        const notesResponse = await getCallNotes(
          session,
          selectedCall.call_uuid,
        );
        setSelectedCallNotes(notesResponse.notes || []);
      } catch (error) {
        const message =
          error instanceof Error ? error.message : "Failed to reprocess audio.";
        setCallDetailError(message);
        Alert.alert("Reprocess Failed", message);
      } finally {
        setReprocessingRecordingId(null);
      }
    },
    [findDocumentationAudioIdForRecording, selectedCall, session],
  );

  React.useEffect(() => {
    if (!callSession.activeCallContext?.callId) {
      return;
    }

    const nextCallId = callSession.activeCallContext.callId;
    setSelectedCallId(nextCallId);
    loadCallDetail(nextCallId, { silent: true, includeExpandedData: false });
    refreshCallsList().catch(() => undefined);
  }, [callSession.activeCallContext?.callId, loadCallDetail, refreshCallsList]);

  React.useEffect(() => {
    const activeProviderCall = callSession.activeCall;
    if (
      !activeProviderCall ||
      selectedCallId !== activeProviderCall.call_uuid
    ) {
      return;
    }

    setSelectedCall(activeProviderCall);
    setCalls((currentCalls) => {
      if (!currentCalls.length) {
        return currentCalls;
      }

      let didUpdate = false;
      const nextCalls = currentCalls.map((callItem) => {
        if (callItem.call_uuid !== activeProviderCall.call_uuid) {
          return callItem;
        }
        didUpdate = true;
        return activeProviderCall;
      });
      return didUpdate ? nextCalls : currentCalls;
    });
  }, [callSession.activeCall, selectedCallId]);

  React.useEffect(() => {
    setCommunicationsAccessUsers([]);
    setCommunicationsAccessTotal(0);
    setCommunicationsAccessBillableSeatCount(0);
    setCommunicationsAccessActionFeedback(null);
    setCommunicationsAccessError(null);
    setMutatingCommunicationsAccessUserUuid(null);
    setSelectedRoutingPhoneNumberId(null);
    setInboundRoutingLoading(false);
    setInboundRoutingSaving(false);
    setInboundRoutingError(null);
    resetInboundRoutingForm();
  }, [
    resetInboundRoutingForm,
    session?.clinicId,
    session?.role,
    session?.userId,
  ]);

  React.useEffect(() => {
    if (!session) {
      return;
    }

    loadCommunicationsPricing().catch(() => undefined);
    loadAssignedPhoneNumbers().catch(() => undefined);
    loadAvailablePhoneNumbers().catch(() => undefined);
    loadUsageSummaryForBillingPeriod().catch(() => undefined);
  }, [
    loadAssignedPhoneNumbers,
    loadAvailablePhoneNumbers,
    loadCommunicationsPricing,
    loadUsageSummaryForBillingPeriod,
    session,
  ]);

  React.useEffect(() => {
    if (!assignedPhoneNumbers.length) {
      setSelectedRoutingPhoneNumberId(null);
      setInboundRoutingError(null);
      resetInboundRoutingForm();
      return;
    }

    if (
      selectedRoutingPhoneNumberId &&
      assignedPhoneNumbers.some(
        (phoneNumber) =>
          phoneNumber.phone_number_id === selectedRoutingPhoneNumberId,
      )
    ) {
      return;
    }

    const fallbackPhoneNumber =
      assignedPhoneNumbers.find((phoneNumber) => phoneNumber.is_primary) ||
      assignedPhoneNumbers[0];
    setSelectedRoutingPhoneNumberId(
      fallbackPhoneNumber?.phone_number_id || null,
    );
  }, [
    assignedPhoneNumbers,
    resetInboundRoutingForm,
    selectedRoutingPhoneNumberId,
  ]);

  React.useEffect(() => {
    if (!session || !isClinicAdmin || !selectedRoutingPhoneNumberId) {
      return;
    }

    loadInboundRoutingForPhoneNumber(selectedRoutingPhoneNumberId).catch(
      () => undefined,
    );
  }, [
    isClinicAdmin,
    loadInboundRoutingForPhoneNumber,
    selectedRoutingPhoneNumberId,
    session,
  ]);

  React.useEffect(() => {
    if (!session || !isClinicAdmin) {
      return;
    }

    loadCommunicationsAccessUsers().catch(() => undefined);
  }, [isClinicAdmin, loadCommunicationsAccessUsers, session]);

  React.useEffect(() => {
    if (!selectedMessageAssignedNumberE164) {
      return;
    }

    if (assignedClinicNumberSet.has(selectedMessageAssignedNumberE164)) {
      return;
    }

    setSelectedMessageAssignedNumberE164(null);
  }, [assignedClinicNumberSet, selectedMessageAssignedNumberE164]);

  React.useEffect(() => {
    if (!selectedCallAssignedNumberE164) {
      return;
    }

    if (assignedClinicNumberSet.has(selectedCallAssignedNumberE164)) {
      return;
    }

    setSelectedCallAssignedNumberE164(null);
  }, [assignedClinicNumberSet, selectedCallAssignedNumberE164]);

  React.useEffect(() => {
    if (
      !isMessagesTab ||
      isMobile ||
      isComposingNewMessage ||
      messagingConversations.selectedConversationUuid ||
      !visibleMessageConversations.length
    ) {
      return;
    }

    messagingConversations.setSelectedConversationUuid(
      visibleMessageConversations[0].conversationUuid,
    );
  }, [
    isMessagesTab,
    isMobile,
    isComposingNewMessage,
    visibleMessageConversations,
    messagingConversations.selectedConversationUuid,
    messagingConversations.setSelectedConversationUuid,
  ]);

  React.useEffect(() => {
    if (
      !isMessagesTab ||
      isComposingNewMessage ||
      !messagingConversations.selectedConversationUuid
    ) {
      return;
    }

    const selectedStillVisible = visibleMessageConversations.some(
      (conversation) =>
        conversation.conversationUuid ===
        messagingConversations.selectedConversationUuid,
    );

    if (!selectedStillVisible && isMobile) {
      messagingConversations.setSelectedConversationUuid(null);
    }
  }, [
    isMessagesTab,
    isComposingNewMessage,
    isMobile,
    messagingConversations.selectedConversationUuid,
    messagingConversations.setSelectedConversationUuid,
    visibleMessageConversations,
  ]);

  React.useEffect(() => {
    if (!selectedCallId) {
      return;
    }

    const selectedStillVisible = visibleCalls.some(
      (call) => call.call_uuid === selectedCallId,
    );
    if (selectedStillVisible) {
      return;
    }

    setSelectedCallId(null);
  }, [selectedCallId, visibleCalls]);

  React.useEffect(() => {
    if (
      !session ||
      !isMessagesTab ||
      isComposingNewMessage ||
      !selectedConversationRecord
    ) {
      return;
    }

    if (!isConversationUnread(selectedConversationRecord)) {
      return;
    }

    const conversationUuid = selectedConversationRecord.conversation_uuid;
    const inFlightConversationUuids = markingReadConversationUuidsRef.current;
    if (inFlightConversationUuids.has(conversationUuid)) {
      return;
    }

    inFlightConversationUuids.add(conversationUuid);
    markMessageConversationRead(conversationUuid, {
      optimistic: true,
      silentRefresh: true,
    })
      .catch(() => undefined)
      .finally(() => {
        inFlightConversationUuids.delete(conversationUuid);
      });
  }, [
    isComposingNewMessage,
    isMessagesTab,
    markMessageConversationRead,
    selectedConversationRecord,
    session,
  ]);

  React.useEffect(() => {
    if (
      !session ||
      communicationsUiState !== "communications_not_initialized" ||
      ftuiViewedTrackedRef.current
    ) {
      return;
    }

    trackCommunicationsEvent("Communications FTUI Viewed");
    ftuiViewedTrackedRef.current = true;
  }, [communicationsUiState, session, trackCommunicationsEvent]);

  React.useEffect(() => {
    if (usageLoading || !usageSummary) {
      return;
    }

    if (overageTrackedStateRef.current === null) {
      overageTrackedStateRef.current = usageOverageActive;
      return;
    }

    if (
      overageTrackedStateRef.current !== usageOverageActive &&
      usageOverageActive
    ) {
      trackCommunicationsEvent("Communications Overage Threshold Crossed", {
        "Billable Minutes": totalBillableMinutes,
        "Included Minutes": includedMinutesCount,
        "Overage Minutes": overageMinutes,
      });
    }

    overageTrackedStateRef.current = usageOverageActive;
  }, [
    includedMinutesCount,
    overageMinutes,
    totalBillableMinutes,
    trackCommunicationsEvent,
    usageLoading,
    usageOverageActive,
    usageSummary,
  ]);

  if (!session) {
    return null;
  }

  const showCallDetail = selectedCallId !== null;
  const normalizedSelectedHangupStatus = (
    selectedCall?.hangup_request_status || ""
  )
    .trim()
    .toLowerCase();
  const canRequestHangup =
    !!selectedCall &&
    (isCallActive(selectedCall.status) ||
      PENDING_HANGUP_STATUSES.has(normalizedSelectedHangupStatus));
  const selectedCallIsActiveSession =
    !!selectedCall &&
    !!callSession.activeCallContext &&
    selectedCall.call_uuid === callSession.activeCallContext.callId;
  const canStartBrowserCall =
    !!fromE164.trim() &&
    dialerE164LooksValid(toE164) &&
    !callSession.isStartingCall;
  const selectedCallPatientName = selectedCall
    ? getPatientNameForCall(selectedCall.patient_uuid)
    : "Unassigned";
  const selectedCallToDisplay = selectedCall
    ? formatPhoneNumberDisplay(selectedCall.to_e164)
    : "-";
  const selectedCallHeaderLabel =
    selectedCallPatientName !== "Unassigned"
      ? selectedCallPatientName
      : selectedCallToDisplay;
  const selectedConversationMessages = conversationMessages.messages;
  const messagesInboxLoading = messagingConversations.loading;
  const messagesInboxError = messagingConversations.error;
  const messagesThreadLoading =
    !!selectedMessageConversation && conversationMessages.loading;
  const messagesThreadError = conversationMessages.error;
  const messageComposerSubmitting = messageComposer.submitting;
  const messageComposerError = messageComposer.error;
  const showMessagesThreadOnMobile =
    isMobile && (!!selectedMessageConversation || isComposingNewMessage);
  const hideMessagesInboxPane =
    isMessagesTab && isMobile && showMessagesThreadOnMobile;
  const hideMessagesThreadPane =
    isMessagesTab && isMobile && !showMessagesThreadOnMobile;
  const useStaticMessagesPane =
    isMessagesTab && isMobile && showMessagesThreadOnMobile;
  const stretchMessagesThreadPane = isMessagesTab && !hideMessagesThreadPane;
  const hideRightPane =
    (!isCallsTab && !isMessagesTab) || hideMessagesThreadPane;
  const hasValidNewMessageRecipient = dialerE164LooksValid(newMessageToE164);
  const messageComposerDisabled =
    !messageFromE164.trim() ||
    (!selectedMessageConversation &&
      (!isComposingNewMessage || !hasValidNewMessageRecipient));
  const activeFeedbackToast =
    assignFeedback ||
    communicationsActionFeedback ||
    communicationsAccessActionFeedback;
  const messageThreadComposer = {
    fromNumbers,
    fromE164: messageFromE164,
    onFromE164Change: (nextFromE164: string) => {
      messageComposer.clearError();
      setMessageFromE164(nextFromE164);
    },
    toCountryCode: messageToCountryCode,
    toLocalNumber: messageToLocalNumber,
    onToCountryCodeChange: (value: string) => {
      messageComposer.clearError();
      setMessageToCountryCode(normalizeDialerCountryCode(value));
    },
    onToLocalNumberChange: (value: string) => {
      messageComposer.clearError();
      setMessageToLocalNumber(normalizeDialerLocalNumber(value));
    },
    draftBody: messageComposerDraft,
    onDraftBodyChange: (nextValue: string) => {
      messageComposer.clearError();
      setMessageComposerDraft(nextValue);
    },
    onSend: () => {
      messageComposer.sendMessage().catch(() => undefined);
    },
    submitting: messageComposerSubmitting,
    disabled: messageComposerDisabled,
    error: messageComposerError,
  };
  const handleOpenAssignMessageConversation = () => {
    if (!selectedMessageConversation) {
      return;
    }
    openAssignConversationPatientModal(selectedMessageConversation);
  };
  const handleMobileMessagesBack = () => {
    messageComposer.clearError();
    setIsComposingNewMessage(false);
    messagingConversations.setSelectedConversationUuid(null);
  };
  const handleRetryMessages = () => {
    conversationMessages.refresh().catch(() => undefined);
  };
  const messageThreadCard = (
    <MessageThreadCard
      isMessagesTab={isMessagesTab}
      loading={messagesThreadLoading}
      error={messagesThreadError}
      selectedConversation={selectedMessageConversation}
      isComposingNewMessage={isComposingNewMessage}
      messages={selectedConversationMessages}
      isMobile={isMobile}
      panelMode
      assignSubmitting={assignSubmitting}
      onOpenAssignPatientModal={handleOpenAssignMessageConversation}
      onMobileBack={handleMobileMessagesBack}
      onRetry={handleRetryMessages}
      formatDateTime={formatDateTime}
      composer={messageThreadComposer}
    />
  );

  return (
    <View style={styles.page}>
      <TitleText text="Conversations" style={styles.pageTitle} />
      <Tab2
        tabs={["Calls", "Messages", "Config", "Usage"]}
        initialTab="Calls"
        onTabSwitch={(tab) =>
          setSelectedCallsTab(tab as "Calls" | "Messages" | "Config" | "Usage")
        }
        tabWidth={
          isMobile
            ? ADMIN_TOKENS.size.searchButtonMinWidth - ADMIN_TOKENS.spacing.sm
            : undefined
        }
        tabPadding={isMobile ? ADMIN_TOKENS.spacing.xs : undefined}
        fontSize={isMobile ? ADMIN_TOKENS.fontSize.sm : undefined}
        containerStyle={styles.callsTabs}
      />

      {pageError ? (
        <View style={styles.errorCard}>
          <Text style={styles.errorText}>{pageError}</Text>
        </View>
      ) : null}
      {activeFeedbackToast ? (
        <ToastMessage
          message={activeFeedbackToast.message}
          tone={activeFeedbackToast.type}
          style={styles.feedbackToast}
        />
      ) : null}

      {initialLoading ? (
        <View style={styles.loadingCard}>
          <ActivityIndicator size="large" color={ADMIN_TOKENS.color.accent} />
          <Text style={styles.loadingText}>Loading call tools...</Text>
        </View>
      ) : (
        <View style={[styles.contentRow, isMobile && styles.contentRowMobile]}>
          <ScrollView
            style={[
              styles.leftPane,
              isCallsTab ? styles.callsLeftPane : null,
              isMessagesTab && !isMobile ? styles.messagesLeftPane : null,
              hideMessagesInboxPane ? styles.hiddenSection : null,
            ]}
            contentContainerStyle={styles.leftPaneContent}
            showsVerticalScrollIndicator={false}
          >
            <CommunicationsSetupCard
              isConfigTab={isConfigTab}
              communicationsUiState={communicationsUiState}
              communicationsPricingLoading={communicationsPricingLoading}
              communicationsPricingError={communicationsPricingError}
              communicationsPricing={communicationsPricing}
              communicationsIsActivated={communicationsIsActivated}
              activationSubmitting={activationSubmitting}
              availableNumbersLoading={availableNumbersLoading}
              availablePhoneNumbersCount={availablePhoneNumbers.length}
              availableNumbersError={availableNumbersError}
              onActivateCommunications={() => {
                handleActivateCommunications().catch(() => undefined);
              }}
              formatCurrencyFromCents={formatCurrencyFromCents}
              formatInteger={formatInteger}
            />

            {isClinicAdmin ? (
              <View style={[styles.card, !isConfigTab && styles.hiddenSection]}>
                <View style={styles.rowBetween}>
                  <Text style={styles.cardTitle}>Communications Access</Text>
                  {communicationsAccessLoading ? (
                    <ActivityIndicator
                      size="small"
                      color={ADMIN_TOKENS.color.accent}
                    />
                  ) : null}
                </View>
                <Text style={styles.metaText}>
                  {COMMUNICATIONS_SEAT_BILLING_COPY}
                </Text>
                <Text style={styles.metaText}>
                  Billable seats:{" "}
                  {formatInteger(communicationsAccessBillableSeatCount)}
                </Text>
                <Text style={styles.metaText}>
                  Clinic users: {formatInteger(communicationsAccessTotal)}
                </Text>

                {communicationsAccessLoading ? (
                  <View style={styles.loadingInlineRow}>
                    <ActivityIndicator
                      size="small"
                      color={ADMIN_TOKENS.color.accent}
                    />
                    <Text style={styles.loadingText}>
                      Loading communications access...
                    </Text>
                  </View>
                ) : communicationsAccessError ? (
                  <View style={styles.sectionStack}>
                    <Text style={styles.inlineErrorText}>
                      {communicationsAccessError}
                    </Text>
                    <Pressable
                      style={styles.callAgainButton}
                      onPress={() => {
                        loadCommunicationsAccessUsers().catch(() => undefined);
                      }}
                      accessibilityLabel="Retry communications access load"
                    >
                      <Text style={styles.callAgainButtonText}>Retry</Text>
                    </Pressable>
                  </View>
                ) : !communicationsAccessUsers.length ? (
                  <Text style={styles.emptyText}>
                    No clinic users found for communications access management.
                  </Text>
                ) : (
                  <View style={styles.listContainer}>
                    {communicationsAccessUsers.map((user) => {
                      const isPatient = user.role === "PATIENT";
                      const isMutating =
                        mutatingCommunicationsAccessUserUuid === user.user_uuid;
                      const isEnabled = user.communications_enabled;

                      return (
                        <View
                          key={`${user.clinic_user_id}-${user.user_uuid}`}
                          style={styles.callRow}
                        >
                          <View style={styles.rowBetween}>
                            <Text style={styles.callNumberText}>
                              {user.user_uuid}
                            </Text>
                            <View
                              style={[
                                styles.statusBadge,
                                isEnabled
                                  ? styles.primaryBadge
                                  : styles.secondaryStatusBadge,
                              ]}
                            >
                              <Text
                                style={[
                                  styles.statusBadgeText,
                                  isEnabled
                                    ? styles.primaryBadgeText
                                    : styles.secondaryStatusBadgeText,
                                ]}
                              >
                                {isEnabled ? "Enabled" : "Disabled"}
                              </Text>
                            </View>
                          </View>
                          <Text style={styles.metaText}>Role: {user.role}</Text>
                          <Text style={styles.metaText}>
                            Billable seat:{" "}
                            {user.is_billable_communications_seat
                              ? "Yes"
                              : "No"}
                          </Text>
                          {isPatient ? (
                            <Text style={styles.emptyText}>
                              {COMMUNICATIONS_PATIENT_DISABLED_REASON}
                            </Text>
                          ) : null}
                          <Pressable
                            style={[
                              styles.callAgainButton,
                              (isPatient || isMutating) &&
                                styles.callAgainButtonDisabled,
                            ]}
                            onPress={() => {
                              handleToggleCommunicationsAccess(user).catch(
                                () => undefined,
                              );
                            }}
                            disabled={isPatient || isMutating}
                            accessibilityLabel={`${isEnabled ? "Disable" : "Enable"} communications access for user ${user.user_uuid}`}
                          >
                            <Text style={styles.callAgainButtonText}>
                              {isMutating
                                ? "Saving..."
                                : isEnabled
                                  ? "Disable Access"
                                  : "Enable Access"}
                            </Text>
                          </Pressable>
                        </View>
                      );
                    })}
                  </View>
                )}
              </View>
            ) : null}

            <PhoneNumbersCard
              isConfigTab={isConfigTab}
              assignedNumbersLoading={assignedNumbersLoading}
              availableNumbersLoading={availableNumbersLoading}
              assignedNumbersError={assignedNumbersError}
              assignedPhoneNumbers={assignedPhoneNumbers}
              includedNumberCount={includedNumberCount}
              activeAssignedNumbersCount={activeAssignedNumbersCount}
              extraAssignedNumbersCount={extraAssignedNumbersCount}
              activationSubmitting={activationSubmitting}
              claimingPhoneNumberId={claimingPhoneNumberId}
              updatingPrimaryNumberId={updatingPrimaryNumberId}
              unassigningPhoneNumberId={unassigningPhoneNumberId}
              communicationsIsActivated={communicationsIsActivated}
              availableNumberSearchTerm={availableNumberSearchTerm}
              onAvailableNumberSearchTermChange={setAvailableNumberSearchTerm}
              availableNumbersError={availableNumbersError}
              filteredAvailablePhoneNumbers={filteredAvailablePhoneNumbers}
              onSetPrimaryNumber={(phoneNumber) => {
                handleSetPrimaryNumber(phoneNumber).catch(() => undefined);
              }}
              onUnassignNumber={(phoneNumber) => {
                handleUnassignNumber(phoneNumber).catch(() => undefined);
              }}
              onClaimAdditionalNumber={(phoneNumber) => {
                handleClaimAdditionalNumber(phoneNumber).catch(() => undefined);
              }}
              formatInteger={formatInteger}
              formatPhoneCapabilities={formatPhoneCapabilities}
            />

            {isClinicAdmin ? (
              <InboundCallRoutingCard
                isConfigTab={isConfigTab}
                assignedPhoneNumbers={assignedPhoneNumbers}
                selectedPhoneNumberId={selectedRoutingPhoneNumberId}
                onSelectPhoneNumberId={setSelectedRoutingPhoneNumberId}
                routingLoading={inboundRoutingLoading}
                routingSaving={inboundRoutingSaving}
                routingError={inboundRoutingError}
                mode={inboundRoutingMode}
                enabled={inboundRoutingEnabled}
                phoneTargetCountryCode={inboundRoutingPhoneTargetCountryCode}
                phoneTargetLocalNumber={inboundRoutingPhoneTargetLocalNumber}
                clinicUserUuid={inboundRoutingClinicUserUuid}
                timeoutSeconds={inboundRoutingTimeoutSeconds}
                selectableClinicUsers={selectableInboundRoutingClinicUsers}
                fieldErrors={inboundRoutingFieldErrors}
                onEnabledChange={handleInboundRoutingEnabledChange}
                onModeChange={handleInboundRoutingModeChange}
                onPhoneTargetCountryCodeChange={
                  handleInboundRoutingPhoneTargetCountryCodeChange
                }
                onPhoneTargetLocalNumberChange={
                  handleInboundRoutingPhoneTargetLocalNumberChange
                }
                onClinicUserUuidChange={handleInboundRoutingClinicUserChange}
                onTimeoutSecondsChange={handleInboundRoutingTimeoutChange}
                onRetryLoad={handleRetryInboundRoutingLoad}
                onSave={() => {
                  handleSaveInboundRouting().catch(() => undefined);
                }}
              />
            ) : null}

            <UsageBillingCard
              isUsageTab={isUsageTab}
              usageStartDate={usageStartDate}
              onUsageStartDateChange={setUsageStartDate}
              usageEndDate={usageEndDate}
              onUsageEndDateChange={setUsageEndDate}
              usageLoading={usageLoading}
              usageError={usageError}
              usageSummary={usageSummary}
              usagePeriodStartLabel={usagePeriodStartLabel}
              usagePeriodEndLabel={usagePeriodEndLabel}
              totalBillableMinutes={totalBillableMinutes}
              includedMinutesCount={includedMinutesCount}
              overageMinutes={overageMinutes}
              usageProgressPercent={usageProgressPercent}
              usageOverageActive={usageOverageActive}
              onApplyUsageDate={handleUsageDateApply}
              formatInteger={formatInteger}
            />

            <MessagesInboxCard
              isMessagesTab={isMessagesTab}
              hideOnMobile={hideMessagesInboxPane}
              loading={messagesInboxLoading}
              error={messagesInboxError}
              conversations={visibleMessageConversations}
              assignedNumberOptions={assignedClinicNumberOptions}
              selectedAssignedNumberE164={selectedMessageAssignedNumberE164}
              selectedConversationUuid={
                messagingConversations.selectedConversationUuid
              }
              selectedSaveStatuses={selectedMessageSaveStatuses}
              showUnreadOnly={showUnreadMessagesOnly}
              assignSubmitting={assignSubmitting}
              isComposingNewMessage={isComposingNewMessage}
              onAssignedNumberChange={setSelectedMessageAssignedNumberE164}
              onSaveStatusesChange={setSelectedMessageSaveStatuses}
              onShowUnreadOnlyChange={setShowUnreadMessagesOnly}
              onStartNewMessage={() => {
                messageComposer.clearError();
                setIsComposingNewMessage(true);
                messagingConversations.setSelectedConversationUuid(null);
              }}
              onSelectConversation={(conversationUuid) => {
                messageComposer.clearError();
                setIsComposingNewMessage(false);
                messagingConversations.setSelectedConversationUuid(
                  conversationUuid,
                );
              }}
              onAssignToPatient={(conversation) => {
                openAssignConversationPatientModal(conversation);
              }}
              onRetry={() => {
                messagingConversations.refresh().catch(() => undefined);
              }}
              formatDateTime={formatDateTime}
            />

            <DialerCard
              isCallsTab={isCallsTab}
              callSession={callSession}
              toCountryCode={toCountryCode}
              onToCountryCodeChange={handleToCountryCodeChange}
              toLocalNumber={toLocalNumber}
              onToLocalNumberChange={handleToLocalNumberChange}
              fromNumbers={fromNumbers}
              fromE164={fromE164}
              onFromE164Change={setFromE164}
              patientSearchTerm={patientSearchTerm}
              onPatientSearchTermChange={handlePatientSearchTermChange}
              onClearPatientSearch={handleClearPatientSearch}
              patientSearchLoading={patientSearch.loading}
              patientSearchError={patientSearch.error}
              patientSearchResults={patientSearch.results}
              patientUuid={patientUuid}
              onSelectPatient={handleSelectPatient}
              selectedPatient={selectedPatient}
              canStartBrowserCall={canStartBrowserCall}
              onStartBrowserCall={() =>
                callSession.startOutboundCall({
                  toE164,
                  fromE164,
                  patientUuid,
                })
              }
            />

            <CallsInboxCard
              isCallsTab={isCallsTab}
              refreshingCalls={refreshingCalls}
              calls={visibleCalls}
              assignedNumberOptions={assignedClinicNumberOptions}
              selectedAssignedNumberE164={selectedCallAssignedNumberE164}
              selectedCallId={selectedCallId}
              assignSubmitting={assignSubmitting}
              hasMoreCalls={hasMoreCalls}
              loadingMoreCalls={loadingMoreCalls}
              callsTotal={callsTotal}
              callsPaginationError={callsPaginationError}
              canStartCall={!callSession.isStartingCall}
              onAssignedNumberChange={setSelectedCallAssignedNumberE164}
              onSelectCallId={setSelectedCallId}
              onOpenAssignPatientModal={openAssignPatientModal}
              onCallAgain={handleCallAgain}
              onLoadMoreCalls={() => {
                loadMoreCalls().catch(() => undefined);
              }}
              getPatientNameForCall={getPatientNameForCall}
              resolveRedialFromNumber={resolveRedialFromNumber}
              formatDateTime={formatDateTime}
              formatPhoneNumberDisplay={formatPhoneNumberDisplay}
            />
          </ScrollView>

          {isMessagesTab && !isMobile ? (
            <View style={styles.messagesPaneDivider} />
          ) : null}

          {useStaticMessagesPane ? (
            <View
              style={[
                styles.rightPane,
                styles.rightPaneBottomAligned,
                hideRightPane ? styles.hiddenSection : null,
              ]}
            >
              {messageThreadCard}
            </View>
          ) : (
            <ScrollView
              style={[
                styles.rightPane,
                isCallsTab ? styles.callsRightPane : null,
                isMessagesTab && !isMobile ? styles.messagesRightPane : null,
                hideRightPane ? styles.hiddenSection : null,
              ]}
              contentContainerStyle={[
                styles.rightPaneContent,
                stretchMessagesThreadPane
                  ? styles.rightPaneContentStretchNoBottomPadding
                  : null,
              ]}
              showsVerticalScrollIndicator={false}
            >
              {isMessagesTab ? (
                messageThreadCard
              ) : (
                <>
                  <CallDetailCard
                    showCallDetail={showCallDetail}
                    callDetailLoading={callDetailLoading}
                    callDetailError={callDetailError}
                    selectedCall={selectedCall}
                    selectedCallHeaderLabel={selectedCallHeaderLabel}
                    canRequestHangup={canRequestHangup}
                    hangupLoading={
                      selectedCallIsActiveSession
                        ? callSession.isHangupSubmitting
                        : hangupSubmitting
                    }
                    assignSubmitting={assignSubmitting}
                    onHangupPress={() => {
                      if (selectedCallIsActiveSession) {
                        callSession.requestHangup().catch(() => undefined);
                        return;
                      }
                      handleRequestHangup().catch(() => undefined);
                    }}
                    onAssignToPatient={openAssignPatientModal}
                    formatDuration={formatDuration}
                    formatDateTime={formatDateTime}
                  />

                  <CallRecordingsCard
                    selectedRecordings={selectedRecordings}
                    selectedCallNotes={selectedCallNotes}
                    expandedCallNoteSummaries={expandedCallNoteSummaries}
                    reprocessingRecordingId={reprocessingRecordingId}
                    onToggleCallNoteSummary={handleToggleCallNoteSummary}
                    onReprocessRecording={handleReprocessRecording}
                    formatDuration={formatDuration}
                    formatDateTime={formatDateTime}
                  />

                  <PatientPhoneCallNotesCard
                    patientNotes={patientNotes}
                    patientAudio={patientAudio}
                    formatDateTime={formatDateTime}
                  />
                </>
              )}
            </ScrollView>
          )}
        </View>
      )}

      <AssignPatientModal
        visible={assignModalVisible}
        submitting={assignSubmitting}
        targetCall={assignTargetCall}
        targetCallId={assignTargetCallId}
        targetConversation={assignTargetConversation}
        patientSearchTerm={assignPatientSearchTerm}
        onPatientSearchTermChange={setAssignPatientSearchTerm}
        patientSearchLoading={assignPatientSearch.loading}
        patientSearchError={assignPatientSearch.error}
        patientOptions={assignPatientOptions}
        selectedPatientUuid={assignSelectedPatientUuid}
        onSelectPatientUuid={setAssignSelectedPatientUuid}
        selectedPatient={assignSelectedPatient}
        onClose={closeAssignPatientModal}
        onAssign={() => {
          handleAssignSelectedPatient().catch(() => undefined);
        }}
      />

      <AssignPhoneOverwriteModal
        visible={!!assignPhoneOverwritePrompt}
        submitting={assignSubmitting}
        patient={assignPhoneOverwritePrompt?.patient || null}
        onRequestClose={() => {
          if (!assignSubmitting) {
            setAssignPhoneOverwritePrompt(null);
          }
        }}
        onKeepExisting={() => {
          handleResolveAssignPhoneOverwrite(false).catch(() => undefined);
        }}
        onOverwrite={() => {
          handleResolveAssignPhoneOverwrite(true).catch(() => undefined);
        }}
      />
    </View>
  );
};

export default HomeConversations;
